/**
 * The API client.
 *
 * Two decisions worth knowing before changing anything here.
 *
 * **The access token lives in a module variable, never in storage.**
 * `localStorage` is readable by any script that reaches the page, so keeping a
 * credential there hands the whole session to a single XSS bug. In memory it
 * dies with the tab, and the httpOnly refresh cookie — which script cannot
 * read at all — is what survives a reload. That is the entire reason the two
 * are split.
 *
 * **A 401 is retried once, through a single shared refresh.** Without the
 * sharing, ten components mounting at once would fire ten refreshes; nine
 * would present an already-rotated token, and the server would read that as
 * token reuse and revoke the session. So concurrent callers await one promise.
 */

import type {
  AbstractEdit,
  AbstractGroupRole,
  AbstractImportResult,
  AbstractReviewChange,
  AbstractStatus,
  AbstractSubmission,
  Abstracts,
  AcceptanceEmails,
  AcceptanceKind,
  AcceptanceSendResult,
  ActivityDay,
  ActivityFilters,
  ActivityRecord,
  AddedAttendee,
  AssetRecord,
  AssignmentInput,
  AssignmentRecord,
  AssignmentRole,
  AttendanceSheet,
  AttendeeFilter,
  AttendeeMessage,
  AttendeePage,
  AttendeePatch,
  AttendeePayment,
  AttendeeRecordData,
  AudienceGroup,
  AudienceGroupDetail,
  BackupRetention,
  BadgeRun,
  BadgeWhere,
  Block,
  BlockCandidates,
  BlockInput,
  BlockPoll,
  Brand,
  Branding,
  Broadcast,
  BroadcastDraft,
  BroadcastKept,
  BroadcastPreview,
  Certificate,
  CertificateDocument,
  CertificatePage,
  CertificateRun,
  CheckoutStarted,
  CheckoutStatus,
  ConfirmationRun,
  ContactImportReport,
  ContactList,
  Cost,
  CostInput,
  Costs,
  Coupon,
  CouponInput,
  CurrentUser,
  DeskAccount,
  DeskAccountIn,
  DeskAccountPatch,
  DocumentLayout,
  Earnings,
  EnquiryRecord,
  EnquiryState,
  Entry,
  EntryData,
  EntryUse,
  EvaluationForm,
  EvaluationLink,
  EvaluationResults,
  EventInput,
  EventPayments,
  EventRecord,
  EventSession,
  EventVisits,
  EventWebAnalytics,
  FallbackReport,
  FeeBand,
  FeeBandInput,
  FileLink,
  FmFile,
  FmFileChange,
  FmFileDownload,
  FmFilesBegin,
  FmFilesIssued,
  FmFilesOverview,
  FmFilesPending,
  FormEvent,
  Gala,
  Gallery,
  GalleryPhoto,
  GroupAdded,
  ImportReport,
  JobLog,
  JobStatus,
  KioskState,
  Member,
  MemoryProfile,
  MemoryReport,
  MessageLog,
  NotifySchedule,
  NotifyScheduleInput,
  OnboardingBoard,
  PaymentLink,
  PaymentLinkSent,
  PaymentsCheck,
  PaymentsLogDay,
  PendingUpload,
  PersonAnswers,
  PersonDocument,
  PersonInput,
  PersonRecord,
  PhotoReady,
  PhotoUpload,
  PlacedFieldData,
  Portrait,
  PreviewToken,
  ProfileLink,
  ProfileLinkInput,
  ProfileLinkPatch,
  ProfileSubmission,
  PublicAttend,
  PublicBlockFeed,
  PublicBlockPanel,
  PublicCall,
  PublicFeed,
  PublicInvitation,
  PublicLink,
  PublicPanel,
  PublicProfileForm,
  PublicRegistrationForm,
  PublicSession,
  PublicSurvey,
  PublicTakePart,
  PublishChanges,
  PublishCheck,
  PublishResult,
  Qa,
  QaLinks,
  QaQuestion,
  QuestionStatus,
  RegistrationConfirmation,
  RegistrationField,
  RegistrationLink,
  RegistrationOffer,
  RegistrationSettings,
  Release,
  ReleaseChanges,
  Replaced,
  Resources,
  ReviewerQueue,
  RevisionRecord,
  RoDocument,
  Role,
  RouteLoadReport,
  RunningOrder,
  SavedMessage,
  SavedMessageInput,
  ScanInput,
  ScanLookup,
  ScanResult,
  Segment,
  SessionInput,
  SiteAudience,
  SiteDescriptor,
  SiteInput,
  SiteSettings,
  StaffActivity,
  StorageReport,
  SubmissionStatus,
  SurveyReport,
  SurveySetup,
  SystemHistory,
  SystemStatus,
  TakePartDetail,
  TakePartEdit,
  TakePartItem,
  TakePartKind,
  TeamComment,
  TeamConference,
  TeamFolder,
  TeamFolderChange,
  TeamFolderView,
  TeamMember,
  TeamPerson,
  TeamStorageFile,
  TeamStorageFileChange,
  TeamTask,
  TeamTaskChange,
  TeamTaskInput,
  TeamTreeFolder,
  TravelBoard,
  TravelInput,
  TravelPlan,
  UnreachableAttendee,
  UploadForm,
  WaitingEntry,
  WelcomePreview,
  WelcomeSent,
  WelcomeText,
  Workshop,
  WorkshopInput,
} from "@/lib/types";
import type { Design, DesignInput } from "./design";

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Raised for a 422, carrying the per-field problems a form can display. */
export class ValidationError extends ApiError {
  constructor(
    readonly problems: { path: (string | number)[]; message: string }[],
    body?: unknown,
  ) {
    super(422, "Validation failed", body);
    this.name = "ValidationError";
  }
}

function authHeaders(): HeadersInit {
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function problemsFrom(body: unknown): { path: (string | number)[]; message: string }[] {
  if (typeof body !== "object" || body === null) return [];
  const record = body as Record<string, unknown>;
  const direct = record.errors;
  if (Array.isArray(direct)) return direct as { path: (string | number)[]; message: string }[];
  const detail = record.detail;
  // The operational endpoints validate their bodies with the framework's own
  // model rather than through the content pipeline, so their 422 is a list of
  // {loc, msg} under `detail` — `loc` prefixed with "body". Without this the
  // form shows "Validation failed" and nothing beside the field that caused
  // it, which is a dead end for whoever typed the value.
  if (Array.isArray(detail)) {
    return detail.map((item) => {
      const problem = item as { loc?: unknown; msg?: unknown };
      const loc = Array.isArray(problem.loc) ? problem.loc.slice(1) : [];
      return {
        path: loc as (string | number)[],
        message: typeof problem.msg === "string" ? problem.msg : "This value was rejected.",
      };
    });
  }
  // Publishing nests its problems one level deeper, under `detail`.
  if (typeof detail === "object" && detail !== null) {
    const nested = (detail as Record<string, unknown>).problems;
    if (Array.isArray(nested))
      return nested as { path: (string | number)[]; message: string }[];
  }
  return [];
}

function messageFrom(body: unknown, fallback: string): string {
  if (typeof body === "string" && body) return body;
  if (typeof body === "object" && body !== null) {
    const detail = (body as Record<string, unknown>).detail;
    if (typeof detail === "string") return detail;
    // A `detail` object carrying its own sentence, which several endpoints
    // send so the caller gets the message *and* the ids it names — the slug
    // conflict, the similar-venue 409, the duplicate assignment. Unwrapping
    // only the string form left those showing `response.statusText`, so a
    // considered "This person is already a speaker at this event." reached the
    // editor as the word "Conflict".
    if (typeof detail === "object" && detail !== null) {
      const message = (detail as Record<string, unknown>).message;
      if (typeof message === "string" && message) return message;
    }
  }
  return fallback;
}

/** How long a refresh may take before it is abandoned, as a page's file may
 * (`lazyPage`'s `PATIENCE_MS`): one held forever would hold every tab's lock. */
export const REFRESH_PATIENCE_MS = 15_000;

/** The refresh, one tab at a time. The cookie is shared by every tab, so two
 * tabs loading at once presented one cookie twice and the server took that for
 * a stolen copy (D-154, 2026-10-26). Under the lock the second tab sends the
 * cookie the first was just handed. Browsers without the Locks API refresh as
 * before, and the server's one-minute grace still covers them. */
async function postRefresh(): Promise<Response> {
  const send = async () => {
    const abandon = new AbortController();
    const timer = setTimeout(() => abandon.abort(), REFRESH_PATIENCE_MS);
    try {
      return await fetch("/v1/auth/refresh", { method: "POST", signal: abandon.signal });
    } finally {
      clearTimeout(timer);
    }
  };
  return "locks" in navigator ? navigator.locks.request("fm-refresh", send) : send();
}

async function refreshAccessToken(): Promise<boolean> {
  // One in-flight refresh, shared. Parallel refreshes would present the same
  // rotated token twice, which the server correctly treats as theft.
  refreshing ??= (async () => {
    try {
      const response = await postRefresh();
      if (!response.ok) {
        accessToken = null;
        return false;
      }
      const body = (await response.json()) as { access_token: string };
      accessToken = body.access_token;
      return true;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  return (await exchange(path, init)).body as T;
}

/** `request`, with the answer's headers beside its body, for the reads that
 * say something there — how many people match beyond the page. */
async function exchange(
  path: string,
  init: RequestInit = {},
  retry = true,
): Promise<{ body: unknown; headers: Headers }> {
  const response = await fetch(path, {
    ...init,
    headers: {
      // Not for FormData: the browser writes its own content type there,
      // including the multipart boundary, and a hand-set one has no boundary
      // for the server to split the parts on.
      ...(init.body && !(init.body instanceof FormData)
        ? { "Content-Type": "application/json" }
        : {}),
      ...authHeaders(),
      ...init.headers,
    },
  });

  if (response.status === 401 && retry && (await refreshAccessToken())) {
    return exchange(path, init, false);
  }

  const body = await parseBody(response);

  if (response.status === 422) {
    throw new ValidationError(problemsFrom(body), body);
  }
  if (!response.ok) {
    throw new ApiError(response.status, messageFrom(body, response.statusText), body);
  }
  return { body, headers: response.headers };
}

/** What the server called the file, from the header it names it in.
 *
 * Exported so it can be tested: the browser has no API for reading a download's
 * name, so an anchor with the wrong `download` attribute silently saves
 * `attendees.csv` over yesterday's export and nobody notices until they open
 * the wrong one. Falls back rather than throwing, because a file with a dull
 * name still holds the right rows.
 */
export function filenameFrom(disposition: string | null, fallback: string): string {
  const match = disposition?.match(/filename="([^"]+)"/);
  return match?.[1] ?? fallback;
}

export interface Download {
  blob: Blob;
  filename: string;
}

/** A file rather than a document.
 *
 * Written beside `request` rather than through it because the body is bytes,
 * not JSON — but it repeats that function's 401-then-refresh deliberately. An
 * export is the one call in this client that does not go through `request`, so
 * it is the one that would quietly stop working fifteen minutes after signing
 * in while every other screen carried on. `api.test.ts` asserts the retry.
 */
async function download(path: string, fallback: string, retry = true): Promise<Download> {
  const response = await fetch(path, { headers: authHeaders() });

  if (response.status === 401 && retry && (await refreshAccessToken())) {
    return download(path, fallback, false);
  }
  if (!response.ok) {
    throw new ApiError(
      response.status,
      messageFrom(await parseBody(response), response.statusText),
    );
  }
  return {
    blob: await response.blob(),
    filename: filenameFrom(response.headers.get("Content-Disposition"), fallback),
  };
}

const json = (data: unknown) => JSON.stringify(data);

/** Where a group lives: an event, or (absent) the company (D-74). */
export interface GroupPlace {
  site: string;
  eventId: string;
}

/** One page of the directory. `limit` is required: 1 to 500. */
export interface PeopleQuery {
  search?: string;
  includeArchived?: boolean;
  limit: number;
  offset?: number;
}

export interface PeoplePage {
  people: PersonRecord[];
  /** Everybody the search matches, beyond this page too. */
  total: number;
}

const groupsBase = (at?: GroupPlace) =>
  at ? `/v1/sites/${at.site}/events/${at.eventId}/groups` : "/v1/groups";

/** An attendee filter as a query string.
 *
 * One place, because the list, the segment counts and the export all send the
 * same filter — written per call site, a dimension reaches the table and not
 * the file that is supposed to be the same set of people.
 */
function filterParams(filter: AttendeeFilter): URLSearchParams {
  const params = new URLSearchParams();
  if (filter.q) params.set("q", filter.q);
  if (filter.status) params.set("status", filter.status);
  // Compared against undefined rather than tested for truth: `false` is a
  // real filter here — "everyone who never checked in" — and a truthiness
  // check would drop exactly the segment I-8 says is missing.
  if (filter.attended !== undefined) params.set("attended", String(filter.attended));
  if (filter.unsubscribed !== undefined) {
    params.set("unsubscribed", String(filter.unsubscribed));
  }
  if (filter.certificate_downloaded !== undefined) {
    params.set("certificate_downloaded", String(filter.certificate_downloaded));
  }
  if (filter.workshop) params.set("workshop", filter.workshop);
  if (filter.registered_from) params.set("registered_from", filter.registered_from);
  if (filter.registered_to) params.set("registered_to", filter.registered_to);
  // Both halves or neither: the server refuses one on its own, because a
  // question with no answer matches nobody and reads as an empty segment.
  if (filter.answer_field && filter.answer_value) {
    params.set("answer_field", filter.answer_field);
    params.set("answer_value", filter.answer_value);
  }
  if (filter.present_on) params.set("present_on", filter.present_on);
  if (filter.source) params.set("source", filter.source);
  return params;
}

export const api = {
  hasToken: () => accessToken !== null,

  async login(email: string, password: string): Promise<void> {
    const response = await fetch("/v1/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: json({ email, password }),
    });
    const body = await parseBody(response);
    if (!response.ok) {
      throw new ApiError(response.status, messageFrom(body, "Could not sign in."), body);
    }
    accessToken = (body as { access_token: string }).access_token;
  },

  /** Called once on load: the refresh cookie is what survives a reload. */
  async restoreSession(): Promise<boolean> {
    return refreshAccessToken();
  },

  async logout(): Promise<void> {
    accessToken = null;
    await fetch("/v1/auth/logout", { method: "POST" });
  },

  /** What the sign-in page may offer; reset only where mail can be sent. */
  authOptions: () => publicRequest<{ password_reset: boolean }>("/v1/auth/options"),

  /** Ask for a link to set a new password. The server answers every address
   * alike, so its message is shown as it comes. */
  forgotPassword: (email: string) =>
    publicRequest<{ message: string }>("/v1/auth/password/forgot", {
      method: "POST",
      body: json({ email }),
    }),

  /** Spend a mailed link on a new password. It signs nobody in: the person
   * signs in afterwards with what they have just typed, which proves they can. */
  resetPassword: (token: string, password: string) =>
    publicRequest<null>("/v1/auth/password/reset", {
      method: "POST",
      body: json({ token, password }),
    }),

  /** An invited administrator's name and password, on the mailed link (D-62). */
  acceptInvite: (token: string, name: string, password: string) =>
    publicRequest<null>("/v1/auth/invite/accept", {
      method: "POST",
      body: json({ token, name, password }),
    }),

  me: () => request<CurrentUser>("/v1/auth/me"),

  siteSchema: (site: string) => request<SiteDescriptor>(`/v1/sites/${site}/schema`),

  // Content is addressed through its event (D10). The site stays in the path
  // because it is what the caller is authorised against; the event says which
  // conference's `welcome` is being asked for.

  listEntries: (site: string, eventId: string, collection: string) =>
    request<Entry[]>(`/v1/sites/${site}/events/${eventId}/collections/${collection}/entries`),

  createEntry: (site: string, eventId: string, collection: string, data: EntryData) =>
    request<Entry>(`/v1/sites/${site}/events/${eventId}/collections/${collection}/entries`, {
      method: "POST",
      body: json(data),
    }),

  updateEntry: (
    site: string,
    eventId: string,
    collection: string,
    id: string,
    data: EntryData,
  ) =>
    request<Entry>(
      `/v1/sites/${site}/events/${eventId}/collections/${collection}/entries/${id}`,
      { method: "PUT", body: json(data) },
    ),

  deleteEntry: (site: string, eventId: string, collection: string, id: string) =>
    request<null>(
      `/v1/sites/${site}/events/${eventId}/collections/${collection}/entries/${id}`,
      { method: "DELETE" },
    ),

  reorder: (site: string, eventId: string, collection: string, entryIds: string[]) =>
    request<null>(`/v1/sites/${site}/events/${eventId}/collections/${collection}/order`, {
      method: "POST",
      body: json({ entry_ids: entryIds }),
    }),

  readSingleton: (site: string, eventId: string, name: string) =>
    request<EntryData>(`/v1/sites/${site}/events/${eventId}/singletons/${name}`),

  writeSingleton: (site: string, eventId: string, name: string, data: EntryData) =>
    request<EntryData>(`/v1/sites/${site}/events/${eventId}/singletons/${name}`, {
      method: "PUT",
      body: json(data),
    }),

  publish: (site: string, eventId: string, note: string | null) =>
    request<PublishResult>(`/v1/sites/${site}/events/${eventId}/publish`, {
      method: "POST",
      body: json({ note }),
    }),

  releases: (site: string, eventId: string) =>
    request<Release[]>(`/v1/sites/${site}/events/${eventId}/releases`),

  releaseChanges: (site: string, eventId: string, version: number) =>
    request<ReleaseChanges>(`/v1/sites/${site}/events/${eventId}/releases/${version}/changes`),

  listAssets: (site: string) => request<AssetRecord[]>(`/v1/sites/${site}/assets`),

  // Minted from the event so the URL it returns points at this event's drafts.
  // The grant itself is still the site's — see the note on the endpoint.
  previewToken: (site: string, eventId: string) =>
    request<PreviewToken>(`/v1/sites/${site}/events/${eventId}/preview-token`, {
      method: "POST",
    }),

  publishCheck: (site: string, eventId: string) =>
    request<PublishCheck>(`/v1/sites/${site}/events/${eventId}/check`),

  /** Every entry on the site that points at this entry, file or person. */
  uses: (site: string, targetId: string) =>
    request<EntryUse[]>(`/v1/sites/${site}/uses/${encodeURIComponent(targetId)}`),

  publishChanges: (site: string, eventId: string) =>
    request<PublishChanges>(`/v1/sites/${site}/events/${eventId}/changes`),

  revisions: (site: string, eventId: string, collection: string, entryId: string) =>
    request<RevisionRecord[]>(
      `/v1/sites/${site}/events/${eventId}/collections/${collection}/entries/${entryId}/revisions`,
    ),

  restoreRevision: (
    site: string,
    eventId: string,
    collection: string,
    entryId: string,
    revisionId: string,
  ) =>
    request<{ id: string; data: Record<string, unknown> }>(
      `/v1/sites/${site}/events/${eventId}/collections/${collection}/entries/${entryId}/revisions/${revisionId}/restore`,
      { method: "POST" },
    ),

  activity: (site: string) => request<ActivityRecord[]>(`/v1/sites/${site}/activity`),

  assignments: (site: string, eventId: string, role?: AssignmentRole) => {
    const suffix = role ? `?role=${role}` : "";
    return request<AssignmentRecord[]>(
      `/v1/sites/${site}/events/${eventId}/assignments${suffix}`,
    );
  },

  assignPerson: (site: string, eventId: string, body: AssignmentInput) =>
    request<AssignmentRecord>(`/v1/sites/${site}/events/${eventId}/assignments`, {
      method: "POST",
      body: json(body),
    }),

  updateAssignment: (
    site: string,
    eventId: string,
    id: string,
    patch: { category?: string | null; position?: number; note?: string | null },
  ) =>
    request<AssignmentRecord>(`/v1/sites/${site}/events/${eventId}/assignments/${id}`, {
      method: "PATCH",
      body: json(patch),
    }),

  /**
   * Arrange one role's cards. The whole list, because the server compares it
   * against the membership it holds and refuses a partial one — a screen
   * sending only what moved cannot say what it was arranged against (T-3b).
   */
  reorderAssignments: (
    site: string,
    eventId: string,
    role: AssignmentRole,
    assignmentIds: readonly string[],
  ) =>
    request<AssignmentRecord[]>(`/v1/sites/${site}/events/${eventId}/assignments/order`, {
      method: "PUT",
      body: json({ role, assignment_ids: assignmentIds }),
    }),

  unassign: (site: string, eventId: string, id: string) =>
    request<void>(`/v1/sites/${site}/events/${eventId}/assignments/${id}`, {
      method: "DELETE",
    }),

  // Not under /sites/, for the same reason `people` is not: an enquiry belongs
  // to no site. The conference it asks about is the thing being requested.
  listEnquiries: (status?: EnquiryState) =>
    request<EnquiryRecord[]>(`/v1/enquiries${status ? `?status=${status}` : ""}`),

  triageEnquiry: (id: string, status: EnquiryState) =>
    request<EnquiryRecord>(`/v1/enquiries/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }),

  // Not under /sites/: the directory is shared across every site (D9). Always
  // a page, never the whole: the directory is every client's and grows with
  // the business (docs/PERFORMANCE.md F4).
  async listPeople({
    search = "",
    includeArchived = false,
    limit,
    offset = 0,
  }: PeopleQuery): Promise<PeoplePage> {
    const query = new URLSearchParams({ limit: String(limit) });
    if (search) query.set("search", search);
    if (includeArchived) query.set("include_archived", "true");
    if (offset) query.set("offset", String(offset));
    const { body, headers } = await exchange(`/v1/people?${query}`);
    const people = body as PersonRecord[];
    return { people, total: Number(headers.get("X-Total-Count") ?? people.length) };
  },

  // `site` is which site the editor is working on, not which site owns the
  // person — nobody owns them. The server records it as their origin, and it
  // is one of the three things that make their contact details readable. Sent
  // on every create, because without it the address just typed comes straight
  // back blank.
  createPerson: (person: PersonInput, site: string) =>
    request<PersonRecord>(`/v1/people?site=${encodeURIComponent(site)}`, {
      method: "POST",
      body: json(person),
    }),

  person: (id: string) => request<PersonRecord>(`/v1/people/${id}`),

  /** Administrators only: the others are deleted (D-39). */
  mergePeople: (keep: string, merge: string[]) =>
    request<PersonRecord>(`/v1/people/${keep}/merge`, {
      method: "POST",
      body: json({ merge }),
    }),

  updatePerson: (id: string, patch: Partial<PersonInput>) =>
    request<PersonRecord>(`/v1/people/${id}`, { method: "PATCH", body: json(patch) }),

  updateAsset: (site: string, id: string, patch: { alt?: string; credit?: string }) =>
    request<AssetRecord>(`/v1/sites/${site}/assets/${id}`, {
      method: "PATCH",
      body: json(patch),
    }),

  deleteAsset: (site: string, id: string) =>
    request<null>(`/v1/sites/${site}/assets/${id}`, { method: "DELETE" }),

  /** Reserve, upload straight to storage, then confirm.
   *
   * The middle step is a plain fetch with no Authorization header on purpose:
   * the presigned URL carries its own signature, and an extra auth header
   * makes S3 reject the request outright. */
  async uploadAsset(site: string, file: File): Promise<AssetRecord> {
    const reserved = await request<{ asset_id: string; upload_url: string }>(
      `/v1/sites/${site}/assets/uploads`,
      {
        method: "POST",
        body: json({ filename: file.name, content_type: file.type, bytes: file.size }),
      },
    );

    const put = await fetch(reserved.upload_url, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": file.type },
    });
    if (!put.ok) {
      throw new ApiError(put.status, "The file could not be uploaded. Please try again.");
    }

    return request<AssetRecord>(`/v1/sites/${site}/assets/${reserved.asset_id}/complete`, {
      method: "POST",
    });
  },

  rollback: (site: string, eventId: string, version: number) =>
    request<PublishResult>(`/v1/sites/${site}/events/${eventId}/releases/${version}/rollback`, {
      method: "POST",
    }),

  /** Newest first, which is the order the year-grouped dashboard reads in. */
  listEvents: (site: string) => request<EventRecord[]>(`/v1/sites/${site}/events`),

  /** Administrators only. */
  createSite: (input: SiteInput) =>
    request<{ key: string; name: string; event: EventRecord }>("/v1/sites", {
      method: "POST",
      body: json(input),
    }),

  /** The fields an event cannot be made without; the rest take the server's
   * defaults (BACKLOG 111: a conference's first event). */
  createEvent: (
    site: string,
    input: Pick<EventInput, "name" | "slug" | "starts_on" | "ends_on"> & Partial<EventInput>,
  ) => request<EventRecord>(`/v1/sites/${site}/events`, { method: "POST", body: json(input) }),

  /** The server applies only the fields it is sent. This sends every one. */
  updateEvent: (site: string, eventId: string, input: EventInput) =>
    request<EventRecord>(`/v1/sites/${site}/events/${eventId}`, {
      method: "PATCH",
      body: json(input),
    }),

  registrationForm: (site: string, eventId: string) =>
    request<{ fields: RegistrationField[] }>(
      `/v1/sites/${site}/events/${eventId}/registration-form`,
    ).then((body) => body.fields),

  /** A whole-form replace, because the order of the questions is part of the
   * form and there is no useful partial expression of "move this one up". */
  saveRegistrationForm: (site: string, eventId: string, fields: RegistrationField[]) =>
    request<{ fields: RegistrationField[] }>(
      `/v1/sites/${site}/events/${eventId}/registration-form`,
      { method: "PUT", body: json({ fields }) },
    ).then((body) => body.fields),

  /** One delegate, added by hand. The body is the event's own questions
   * answered, so there is no model here to drift from the declaration. */
  addAttendee: (site: string, eventId: string, answers: Record<string, unknown>) =>
    request<AddedAttendee>(`/v1/sites/${site}/events/${eventId}/attendees`, {
      method: "POST",
      body: json(answers),
    }),

  attendees: (
    site: string,
    eventId: string,
    filter: AttendeeFilter = {},
    cursor?: string | null,
  ) => {
    const params = filterParams(filter);
    if (cursor) params.set("cursor", cursor);
    const query = params.toString();
    return request<AttendeePage>(
      `/v1/sites/${site}/events/${eventId}/attendees${query ? `?${query}` : ""}`,
    );
  },

  /** The export tiles, counted (T-28).
   *
   * Anything passed here narrows every tile at once, which is how the date
   * range works. Each tile comes back carrying the query string that reproduces
   * it, so `exportAttendees` below is handed that rather than composing one —
   * two definitions of "unpaid" is a tile that counts one set of people and
   * downloads another.
   */
  segments: (site: string, eventId: string, filter: AttendeeFilter = {}) => {
    const query = filterParams(filter).toString();
    return request<Segment[]>(
      `/v1/sites/${site}/events/${eventId}/exports/segments${query ? `?${query}` : ""}`,
    );
  },

  /** One segment as a spreadsheet.
   *
   * `params` is a tile's own `filter`, passed through untouched. `segment`
   * names the file only.
   */
  exportAttendees: (
    site: string,
    eventId: string,
    params: Record<string, string>,
    segment: string,
  ) => {
    const query = new URLSearchParams({ ...params, segment }).toString();
    return download(
      `/v1/sites/${site}/events/${eventId}/exports/attendees.csv?${query}`,
      "attendees.csv",
    );
  },

  /** A sheet whose headings are this event's own questions (P5), named as the
   * column step guesses them, so a filled-in copy maps itself. */
  importTemplate: (site: string, eventId: string) =>
    download(
      `/v1/sites/${site}/events/${eventId}/attendees/import/template`,
      "import-template.csv",
    ),

  /** The file's header row, so a mapping step can be offered before anything
   * is validated against it. Nothing is read but the first line. */
  importColumns: (site: string, eventId: string, file: File) => {
    const body = new FormData();
    body.append("file", file);
    return request<string[]>(`/v1/sites/${site}/events/${eventId}/attendees/import/columns`, {
      method: "POST",
      body,
    });
  },

  /** Import a spreadsheet, or say what importing it would do.
   *
   * `dryRun` is the server's own boolean and the only difference between the
   * two: same parsing, same validation, same duplicate checks, and a rollback
   * instead of a commit at the end. That is what makes the preview true. */
  importAttendees: (
    site: string,
    eventId: string,
    file: File,
    mapping: Record<string, string>,
    dryRun: boolean,
    updateExisting: boolean,
  ) => {
    const body = new FormData();
    body.append("file", file);
    body.append("mapping", json(mapping));
    body.append("dry_run", String(dryRun));
    body.append("update_existing", String(updateExisting));
    return request<ImportReport>(`/v1/sites/${site}/events/${eventId}/attendees/import`, {
      method: "POST",
      body,
    });
  },

  /** This event's gala replies and its headcount (T-25).
   *
   * Reached through the event, so the server's query carries `event_id` — their
   * `/gala/view` renders one event's header over another's rows (I-11). */
  gala: (site: string, eventId: string) =>
    request<Gala>(`/v1/sites/${site}/events/${eventId}/gala`),

  galaForm: (site: string, eventId: string) =>
    request<{ fields: RegistrationField[] }>(`/v1/sites/${site}/events/${eventId}/gala-form`),

  saveGalaForm: (site: string, eventId: string, fields: RegistrationField[]) =>
    request<{ fields: RegistrationField[] }>(`/v1/sites/${site}/events/${eventId}/gala-form`, {
      method: "PUT",
      body: json({ fields }),
    }),

  galaLink: (site: string, eventId: string) =>
    request<PublicLink>(`/v1/sites/${site}/events/${eventId}/gala-link`),

  /** Issuing a new token *is* revoking the old one — one operation, not two. */
  issueGalaLink: (site: string, eventId: string) =>
    request<PublicLink>(`/v1/sites/${site}/events/${eventId}/gala-link`, {
      method: "POST",
    }),

  /** The link stops resolving; the replies stay. */
  revokeGalaLink: (site: string, eventId: string) =>
    request<PublicLink>(`/v1/sites/${site}/events/${eventId}/gala-link`, {
      method: "DELETE",
    }),

  /** This event's survey results (T-24).
   *
   * Their `/survey/results?event=45` serves all 257 responses and every
   * free-text comment to a fetch with no cookies (I-1). This one is reached
   * through the event on the authenticated path. */
  surveyReport: (site: string, eventId: string) =>
    request<SurveyReport>(`/v1/sites/${site}/events/${eventId}/survey`),

  /** Every survey response as a spreadsheet, a row each, as the old system
   * exported them: who answered (blank on an anonymous survey), each answer
   * under its question, and when. */
  surveySheet: (site: string, eventId: string) =>
    download(`/v1/sites/${site}/events/${eventId}/survey.csv`, "survey.csv"),

  surveyForm: (site: string, eventId: string) =>
    request<SurveySetup>(`/v1/sites/${site}/events/${eventId}/survey-form`),

  /** The questions and the two choices in one write, because they are one
   * decision made on one screen. A *change* to `anonymous` or
   * `standard_questions` after somebody has answered is refused with 409. */
  saveSurveyForm: (
    site: string,
    eventId: string,
    form: Omit<SurveySetup, "can_change_anonymity">,
  ) =>
    request<SurveySetup>(`/v1/sites/${site}/events/${eventId}/survey-form`, {
      method: "PUT",
      body: json(form),
    }),

  surveyLink: (site: string, eventId: string) =>
    request<PublicLink>(`/v1/sites/${site}/events/${eventId}/survey-link`),

  issueSurveyLink: (site: string, eventId: string) =>
    request<PublicLink>(`/v1/sites/${site}/events/${eventId}/survey-link`, {
      method: "POST",
    }),

  /** The link stops resolving; the responses stay. */
  revokeSurveyLink: (site: string, eventId: string) =>
    request<PublicLink>(`/v1/sites/${site}/events/${eventId}/survey-link`, {
      method: "DELETE",
    }),

  /** This event's moderation queue, its panel and its tallies (T-26).
   *
   * Their `/qa/live?event=45` is the same feed on an open URL keyed by a small
   * sequential integer (I-1); this one is reached through the event on the
   * authenticated path. */
  qa: (site: string, eventId: string) => request<Qa>(`/v1/sites/${site}/events/${eventId}/qa`),

  /** Approve, reject or mark answered — the server records who and when. */
  moderateQuestion: (
    site: string,
    eventId: string,
    questionId: string,
    status: QuestionStatus,
  ) =>
    request<QaQuestion>(`/v1/sites/${site}/events/${eventId}/qa/questions/${questionId}`, {
      method: "PATCH",
      body: json({ status }),
    }),

  qaLinks: (site: string, eventId: string) =>
    request<QaLinks>(`/v1/sites/${site}/events/${eventId}/qa-links`),

  issueQaLink: (site: string, eventId: string) =>
    request<QaLinks>(`/v1/sites/${site}/events/${eventId}/qa-link`, { method: "POST" }),

  /** Closes submissions. The wall stays up — the two links are independent. */
  revokeQaLink: (site: string, eventId: string) =>
    request<QaLinks>(`/v1/sites/${site}/events/${eventId}/qa-link`, { method: "DELETE" }),

  issueQaFeedLink: (site: string, eventId: string) =>
    request<QaLinks>(`/v1/sites/${site}/events/${eventId}/qa-feed-link`, { method: "POST" }),

  /** Closes the stage feed. People can still ask. */
  revokeQaFeedLink: (site: string, eventId: string) =>
    request<QaLinks>(`/v1/sites/${site}/events/${eventId}/qa-feed-link`, {
      method: "DELETE",
    }),

  /** Every submission to this event, with its scores (T-23).
   *
   * The whole screen in one response, and every write below returns the same
   * shape: a decision changes a status *and* moves the counts beside it, and
   * two responses describing one event at two instants is the shape of I-10. */
  abstracts: (site: string, eventId: string) =>
    request<Abstracts>(`/v1/sites/${site}/events/${eventId}/abstracts`),

  decideAbstract: (
    site: string,
    eventId: string,
    abstractId: string,
    status: AbstractStatus,
    note: string | null,
    /** Oral or poster with an acceptance (D-110); left out keeps what is there. */
    presentation?: "oral" | "poster" | null,
  ) =>
    request<Abstracts>(`/v1/sites/${site}/events/${eventId}/abstracts/${abstractId}`, {
      method: "PATCH",
      body: JSON.stringify(
        presentation === undefined ? { status, note } : { status, note, presentation },
      ),
    }),

  /** Correct an abstract's title, text, authors, presenter or workplace (D-110). */
  editAbstract: (site: string, eventId: string, abstractId: string, edit: AbstractEdit) =>
    request<Abstracts>(`/v1/sites/${site}/events/${eventId}/abstracts/${abstractId}`, {
      method: "PUT",
      body: JSON.stringify(edit),
    }),

  /** An owner's undo (D-128): the abstract as it was before its first edit here. */
  restoreAbstract: (site: string, eventId: string, abstractId: string) =>
    request<Abstracts>(`/v1/sites/${site}/events/${eventId}/abstracts/${abstractId}/edits`, {
      method: "DELETE",
    }),

  /** An owner's undo (D-128): one review taken away; its reviewer may review again. */
  removeAbstractReview: (site: string, eventId: string, abstractId: string, reviewId: string) =>
    request<Abstracts>(
      `/v1/sites/${site}/events/${eventId}/abstracts/${abstractId}/reviews/${reviewId}`,
      { method: "DELETE" },
    ),

  /** An owner's undo (D-128): one poster grade taken away. */
  removePosterGrade: (site: string, eventId: string, abstractId: string, gradeId: string) =>
    request<Abstracts>(
      `/v1/sites/${site}/events/${eventId}/abstracts/${abstractId}/grades/${gradeId}`,
      { method: "DELETE" },
    ),

  /** The review form, guidelines and settings of the abstracts site (D-110). */
  saveAbstractReview: (site: string, eventId: string, change: AbstractReviewChange) =>
    request<Abstracts>(`/v1/sites/${site}/events/${eventId}/abstract-review`, {
      method: "PUT",
      body: JSON.stringify(change),
    }),

  /** An owner puts the committee back to deciding; answers like the screen. */
  unlockAbstractDecisions: (site: string, eventId: string) =>
    request<Abstracts>(`/v1/sites/${site}/events/${eventId}/abstract-review/final`, {
      method: "DELETE",
    }),

  /** The reviewers' shared login: made, or given a new password, the one given
   * or else one made up. Shown once. */
  issueAbstractLogin: (site: string, eventId: string, password?: string) =>
    request<{ login_id: string; password: string }>(
      `/v1/sites/${site}/events/${eventId}/abstract-review/login`,
      { method: "PUT", body: password === undefined ? null : json({ password }) },
    ),

  disableAbstractLogin: (site: string, eventId: string) =>
    request<void>(`/v1/sites/${site}/events/${eventId}/abstract-review/login`, {
      method: "DELETE",
    }),

  /** A shared login by role (D-115): made, or given a new password. Shown here. */
  issueGroupLogin: (
    site: string,
    eventId: string,
    role: AbstractGroupRole,
    password?: string,
  ) =>
    request<{ login_id: string; password: string }>(
      `/v1/sites/${site}/events/${eventId}/abstract-logins/${role}`,
      { method: "PUT", body: password === undefined ? null : json({ password }) },
    ),

  disableGroupLogin: (site: string, eventId: string, role: AbstractGroupRole) =>
    request<void>(`/v1/sites/${site}/events/${eventId}/abstract-logins/${role}`, {
      method: "DELETE",
    }),

  /** An owner reads a login's password; the read is logged. */
  revealGroupLogin: (site: string, eventId: string, role: AbstractGroupRole) =>
    request<{ login_id: string; password: string }>(
      `/v1/sites/${site}/events/${eventId}/abstract-logins/${role}/reveal`,
      { method: "POST" },
    ),

  /** A presenter's own login: made, or its username or password changed. The
   * password comes back only when this call set one. */
  issuePresenterLogin: (
    site: string,
    eventId: string,
    abstractId: string,
    change: { username?: string; password?: string },
  ) =>
    request<{ username: string; password: string | null }>(
      `/v1/sites/${site}/events/${eventId}/abstracts/${abstractId}/presenter-login`,
      { method: "PUT", body: json(change) },
    ),

  disablePresenterLogin: (site: string, eventId: string, abstractId: string) =>
    request<void>(
      `/v1/sites/${site}/events/${eventId}/abstracts/${abstractId}/presenter-login`,
      {
        method: "DELETE",
      },
    ),

  acceptanceEmails: (site: string, eventId: string) =>
    request<AcceptanceEmails>(`/v1/sites/${site}/events/${eventId}/abstract-emails`),

  saveAcceptanceEmails: (
    site: string,
    eventId: string,
    change: Partial<Record<AcceptanceKind, { subject: string; body: string }>>,
  ) =>
    request<AcceptanceEmails>(`/v1/sites/${site}/events/${eventId}/abstract-emails`, {
      method: "PUT",
      body: json(change),
    }),

  /** One email as one presenter would get it, the password masked. */
  previewAcceptanceEmail: (
    site: string,
    eventId: string,
    kind: AcceptanceKind,
    abstractId: string,
  ) =>
    request<{ subject: string; body: string }>(
      `/v1/sites/${site}/events/${eventId}/abstract-emails/preview`,
      { method: "POST", body: json({ kind, abstract_id: abstractId }) },
    ),

  /** Emails every accepted presenter not yet emailed, each exactly once, or
   * only the chosen abstracts' presenters (D-191). */
  sendAcceptanceEmails: (site: string, eventId: string, abstractIds?: string[]) =>
    request<AcceptanceSendResult>(`/v1/sites/${site}/events/${eventId}/abstract-emails/send`, {
      method: "POST",
      ...(abstractIds ? { body: json({ abstract_ids: abstractIds }) } : {}),
    }),

  resendAcceptanceEmail: (site: string, eventId: string, abstractId: string) =>
    request<Abstracts>(`/v1/sites/${site}/events/${eventId}/abstracts/${abstractId}/email`, {
      method: "POST",
    }),

  /** Every abstract as a spreadsheet: reviews summed up, Final decision, email,
   * files and grades; no address and no password (D-115). */
  abstractsSheet: (site: string, eventId: string) =>
    download(`/v1/sites/${site}/events/${eventId}/abstracts.csv`, "abstracts.csv"),

  /** A short-lived link to one presenter upload. */
  presenterUpload: (site: string, eventId: string, fileId: string) =>
    request<FileLink>(`/v1/sites/${site}/events/${eventId}/abstract-files/${fileId}`),

  /** Removes a presenter upload; answers like the screen. */
  removePresenterUpload: (site: string, eventId: string, fileId: string) =>
    request<Abstracts>(`/v1/sites/${site}/events/${eventId}/abstract-files/${fileId}`, {
      method: "DELETE",
    }),

  revealPresenterLogin: (site: string, eventId: string, abstractId: string) =>
    request<{ username: string; password: string }>(
      `/v1/sites/${site}/events/${eventId}/abstracts/${abstractId}/presenter-login/reveal`,
      { method: "POST" },
    ),

  /** Abstracts from an earlier system, keyed by its own ids; counts come back. */
  importAbstracts: (site: string, eventId: string, abstracts: unknown[]) =>
    request<AbstractImportResult>(`/v1/sites/${site}/events/${eventId}/abstract-import`, {
      method: "POST",
      body: JSON.stringify({ abstracts }),
    }),

  /** A short-lived link to one manuscript. Asked for on the click rather than
   * held on the page, because a URL to an unpublished paper should expire. */
  abstractFile: (site: string, eventId: string, abstractId: string) =>
    request<FileLink>(`/v1/sites/${site}/events/${eventId}/abstracts/${abstractId}/file`),

  /** A five-minute link to a document somebody sent with a form (D-30). Editors
   * and owners only, and each one is written to the audit log. */
  /** What somebody sent with this site's profile forms (D-30): the files, not
   * their contents — opening one is `uploadLink`. */
  personDocuments: (personId: string) =>
    request<PersonDocument[]>(`/v1/people/${encodeURIComponent(personId)}/documents`),

  /** What somebody answered beyond the directory's columns and their documents
   * (D-95): a speaker's title, an organiser's own questions. */
  personAnswers: (personId: string) =>
    request<PersonAnswers[]>(`/v1/people/${encodeURIComponent(personId)}/answers`),

  uploadLink: (site: string, uploadId: string) =>
    request<FileLink & { filename: string; bytes: number }>(
      `/v1/sites/${site}/uploads/${encodeURIComponent(uploadId)}`,
    ),

  /** A document sent with a form people fill in, of any conference or none:
   * opened through the directory (2026-10-25). */
  formUploadLink: (uploadId: string) =>
    request<FileLink & { filename: string; bytes: number }>(
      `/v1/people/form-uploads/${encodeURIComponent(uploadId)}`,
    ),

  // FM Files (BACKLOG 166). A viewer may read the overview and download;
  // everything else needs an editor or owner, and the server says so.

  fmFiles: (site: string, eventId: string) =>
    request<FmFilesOverview>(`/v1/sites/${site}/events/${eventId}/fm-files`),

  /** Stop speakers changing their files. Admins still can. */
  closeFmFilesUploads: (site: string, eventId: string) =>
    request<null>(`/v1/sites/${site}/events/${eventId}/fm-files/lock`, { method: "PUT" }),

  openFmFilesUploads: (site: string, eventId: string) =>
    request<null>(`/v1/sites/${site}/events/${eventId}/fm-files/lock`, { method: "DELETE" }),

  /** Make the stage login, or give it a new password (the one given, or else one
   * made up), which also turns it back on. */
  issueFmFilesStage: (site: string, eventId: string, password?: string) =>
    request<FmFilesIssued>(`/v1/sites/${site}/events/${eventId}/fm-files/stage`, {
      method: "PUT",
      body: password === undefined ? null : json({ password }),
    }),

  disableFmFilesStage: (site: string, eventId: string) =>
    request<null>(`/v1/sites/${site}/events/${eventId}/fm-files/stage`, { method: "DELETE" }),

  /** Make a speaker's login, or give it a new password. A new login takes its
   * mobile number from the directory unless `mobile` is given; `null` makes it
   * with none. An existing login keeps its number either way. The password is
   * `password` when given, else one made up. */
  /** Each speaker named, or every speaker, their own sign-in link by email
   * (D-159); `emails` fills addresses the directory lacks, and is kept. */
  sendFmFilesLinks: (
    site: string,
    eventId: string,
    to: string[] | "everyone",
    emails: Record<string, string>,
  ) =>
    request<WelcomeSent>(`/v1/sites/${site}/events/${eventId}/fm-files/links`, {
      method: "POST",
      body: json({ ...(to === "everyone" ? { everyone: true } : { person_ids: to }), emails }),
    }),

  issueFmFilesSpeaker: (
    site: string,
    eventId: string,
    personId: string,
    mobile?: string | null,
    password?: string,
  ) =>
    request<FmFilesIssued>(
      `/v1/sites/${site}/events/${eventId}/fm-files/speakers/${encodeURIComponent(personId)}/login`,
      {
        method: "PUT",
        body: json({
          ...(mobile === undefined ? {} : { mobile }),
          ...(password === undefined ? {} : { password }),
        }),
      },
    ),

  setFmFilesMobile: (site: string, eventId: string, personId: string, mobile: string | null) =>
    request<null>(
      `/v1/sites/${site}/events/${eventId}/fm-files/speakers/${encodeURIComponent(personId)}/login`,
      { method: "PATCH", body: json({ mobile }) },
    ),

  disableFmFilesSpeaker: (site: string, eventId: string, personId: string) =>
    request<null>(
      `/v1/sites/${site}/events/${eventId}/fm-files/speakers/${encodeURIComponent(personId)}/login`,
      { method: "DELETE" },
    ),

  /** Somewhere to PUT a file on a speaker's behalf, whether uploads are open or not. */
  beginFmFile: (site: string, eventId: string, personId: string, body: FmFilesBegin) =>
    request<FmFilesPending>(
      `/v1/sites/${site}/events/${eventId}/fm-files/speakers/${encodeURIComponent(personId)}/files`,
      { method: "POST", body: json(body) },
    ),

  /** The server reads the upload back from storage and accepts or refuses it. */
  finishFmFile: (site: string, eventId: string, fileId: string) =>
    request<FmFile>(
      `/v1/sites/${site}/events/${eventId}/fm-files/files/${encodeURIComponent(fileId)}/finish`,
      { method: "POST" },
    ),

  changeFmFile: (site: string, eventId: string, fileId: string, change: FmFileChange) =>
    request<FmFile>(
      `/v1/sites/${site}/events/${eventId}/fm-files/files/${encodeURIComponent(fileId)}`,
      { method: "PATCH", body: json(change) },
    ),

  removeFmFile: (site: string, eventId: string, fileId: string) =>
    request<null>(
      `/v1/sites/${site}/events/${eventId}/fm-files/files/${encodeURIComponent(fileId)}`,
      { method: "DELETE" },
    ),

  /** A five-minute link that saves the file under its own name. Audited. */
  fmFileDownload: (site: string, eventId: string, fileId: string) =>
    request<FmFileDownload>(
      `/v1/sites/${site}/events/${eventId}/fm-files/files/${encodeURIComponent(fileId)}/download`,
    ),

  openAbstractCall: (site: string, eventId: string) =>
    request<PublicLink>(`/v1/sites/${site}/events/${eventId}/abstract-link`, {
      method: "POST",
    }),

  closeAbstractCall: (site: string, eventId: string) =>
    request<PublicLink>(`/v1/sites/${site}/events/${eventId}/abstract-link`, {
      method: "DELETE",
    }),

  /** What this event earned, from one request (T-16).
   *
   * One call rather than one per tile, so two figures on the screen cannot
   * describe different populations — which is exactly what I-10 is about. */
  earnings: (site: string, eventId: string) =>
    request<Earnings>(`/v1/sites/${site}/events/${eventId}/earnings`),

  /** The payment link open for a registration staff made (plan 3); 404 when none is. */
  paymentLink: (site: string, eventId: string, attendeeId: string) =>
    request<PaymentLink>(
      `/v1/sites/${site}/events/${eventId}/attendees/${attendeeId}/payment-link`,
    ),

  /** *Send payment link*: a MyFatoorah page for what it owes, mailed to them. */
  sendPaymentLink: (site: string, eventId: string, attendeeId: string) =>
    request<PaymentLinkSent>(
      `/v1/sites/${site}/events/${eventId}/attendees/${attendeeId}/payment-link`,
      { method: "POST" },
    ),

  /** *Check connection*: whether MyFatoorah accepts the key in SSM (an administrator's). */
  checkPayments: () => request<PaymentsCheck>("/v1/payments/check", { method: "POST" }),

  /** The website's page views and visitors, as its beacon counted them (BACKLOG 217). */
  eventVisits: (site: string, eventId: string) =>
    request<EventVisits>(`/v1/sites/${site}/events/${eventId}/visits`),

  /** The website's last week as Cloudflare Web Analytics measured it (D-168). */
  eventWebAnalytics: (site: string, eventId: string) =>
    request<EventWebAnalytics>(`/v1/sites/${site}/events/${eventId}/web-analytics`),

  /** Every payment, the held checkouts and the flagged registrations (BACKLOG 213). */
  eventPayments: (site: string, eventId: string) =>
    request<EventPayments>(`/v1/sites/${site}/events/${eventId}/payments`),

  /** A held checkout settled by a person, who says how. */
  resolveCheckout: (site: string, eventId: string, checkoutId: string, resolution: string) =>
    request<{ id: string; resolved_at: string; resolution: string }>(
      `/v1/sites/${site}/events/${eventId}/checkouts/${checkoutId}/resolve`,
      { method: "POST", body: json({ resolution }) },
    ),

  /** A registration's attention flag, cleared once looked at. */
  clearAttention: (site: string, eventId: string, attendeeId: string) =>
    request<null>(`/v1/sites/${site}/events/${eventId}/attendees/${attendeeId}/attention`, {
      method: "DELETE",
    }),

  /** Who marked people paid and checked them in, per member of staff. */
  staffActivity: (site: string, eventId: string) =>
    request<StaffActivity[]>(`/v1/sites/${site}/events/${eventId}/staff-activity`),

  /** What this delegate has paid (T-13, T-14). */
  attendeePayments: (site: string, eventId: string, attendeeId: string) =>
    request<AttendeePayment[]>(
      `/v1/sites/${site}/events/${eventId}/attendees/${attendeeId}/payments`,
    ),

  /** The event's fee bands, in order (D-94). */
  feeBands: (site: string, eventId: string) =>
    request<FeeBand[]>(`/v1/sites/${site}/events/${eventId}/fees`),

  /** A full workshop's waiting list (BACKLOG 191 (6)). */
  waitingList: (site: string, eventId: string, workshopId: string) =>
    request<WaitingEntry[]>(
      `/v1/sites/${site}/events/${eventId}/workshops/${workshopId}/waiting`,
    ),

  joinWaitingList: (site: string, eventId: string, workshopId: string, code: string) =>
    request<WaitingEntry[]>(
      `/v1/sites/${site}/events/${eventId}/workshops/${workshopId}/waiting`,
      { method: "POST", body: json({ code }) },
    ),

  leaveWaitingList: (site: string, eventId: string, workshopId: string, code: string) =>
    request<void>(
      `/v1/sites/${site}/events/${eventId}/workshops/${workshopId}/waiting/${encodeURIComponent(code)}`,
      { method: "DELETE" },
    ),

  /** An event's costs and budget (BACKLOG 191 (4)). */
  costs: (site: string, eventId: string) =>
    request<Costs>(`/v1/sites/${site}/events/${eventId}/costs`),

  createCost: (site: string, eventId: string, input: CostInput) =>
    request<Cost>(`/v1/sites/${site}/events/${eventId}/costs`, {
      method: "POST",
      body: json(input),
    }),

  updateCost: (site: string, eventId: string, id: string, input: CostInput) =>
    request<Cost>(`/v1/sites/${site}/events/${eventId}/costs/${id}`, {
      method: "PATCH",
      body: json(input),
    }),

  deleteCost: (site: string, eventId: string, id: string) =>
    request<void>(`/v1/sites/${site}/events/${eventId}/costs/${id}`, { method: "DELETE" }),

  /** What the registration form sells (BACKLOG 105). */
  registrationOffers: (site: string, eventId: string) =>
    request<{ offers: RegistrationOffer[] }>(
      `/v1/sites/${site}/events/${eventId}/registration-offers`,
    ),

  /** Whether the form asks for a discount code, and offers WhatsApp updates. */
  registrationSettings: (site: string, eventId: string) =>
    request<RegistrationSettings>(`/v1/sites/${site}/events/${eventId}/registration-settings`),

  /** The settings given; one left out is left as it is. */
  setRegistrationSettings: (
    site: string,
    eventId: string,
    change: Partial<RegistrationSettings>,
  ) =>
    request<RegistrationSettings>(`/v1/sites/${site}/events/${eventId}/registration-settings`, {
      method: "PUT",
      body: json(change),
    }),

  /** A Future Minds admin's alone, as prices are. */
  setRegistrationOffers: (site: string, eventId: string, offers: RegistrationOffer[]) =>
    request<{ offers: RegistrationOffer[] }>(
      `/v1/sites/${site}/events/${eventId}/registration-offers`,
      { method: "PUT", body: json({ offers }) },
    ),

  createFeeBand: (site: string, eventId: string, input: FeeBandInput) =>
    request<FeeBand>(`/v1/sites/${site}/events/${eventId}/fees`, {
      method: "POST",
      body: json(input),
    }),

  updateFeeBand: (site: string, eventId: string, id: string, input: Partial<FeeBandInput>) =>
    request<FeeBand>(`/v1/sites/${site}/events/${eventId}/fees/${id}`, {
      method: "PATCH",
      body: json(input),
    }),

  deleteFeeBand: (site: string, eventId: string, id: string) =>
    request<void>(`/v1/sites/${site}/events/${eventId}/fees/${id}`, { method: "DELETE" }),

  /** Mark them paid at the desk: who and when, never an amount (D-94). */
  settleOffline: (site: string, eventId: string, attendeeId: string, body: { note?: string }) =>
    request<AttendeePayment>(
      `/v1/sites/${site}/events/${eventId}/attendees/${attendeeId}/payments/offline`,
      { method: "POST", body: json(body) },
    ),

  /** What this delegate has been sent (T-27). */
  attendeeMessages: (site: string, eventId: string, attendeeId: string) =>
    request<AttendeeMessage[]>(
      `/v1/sites/${site}/events/${eventId}/attendees/${attendeeId}/messages`,
    ),

  /** What a draft would do to a filter-picked audience, without doing it.
   *
   * A POST because the draft is the request: a subject and a body do not belong
   * in a query string, and this changes nothing. The rendering is the server's
   * so that what an organiser approves is what the recipients receive — the
   * same reason the badge editor and the print sheet share one renderer.
   */
  broadcastPreview: (
    site: string,
    eventId: string,
    audience: Record<string, string>,
    draft: BroadcastDraft,
  ) => {
    const query = new URLSearchParams(audience).toString();
    return request<BroadcastPreview>(
      `/v1/sites/${site}/events/${eventId}/broadcasts/preview${query ? `?${query}` : ""}`,
      { method: "POST", body: json(draft) },
    );
  },

  /** Keep a send and queue it for its time. 202: the worker resolves the
   * audience and sends when it is due (D-74).
   *
   * `send_at` is ISO 8601 or absent. Absent means now.
   */
  sendBroadcast: (
    site: string,
    eventId: string,
    audience: Record<string, string>,
    draft: BroadcastDraft,
  ) => {
    const query = new URLSearchParams(audience).toString();
    return request<BroadcastKept>(
      `/v1/sites/${site}/events/${eventId}/broadcasts${query ? `?${query}` : ""}`,
      { method: "POST", body: json(draft) },
    );
  },

  /** The event's sends: `scheduled` the waiting ones, soonest first; `done` the rest. */
  broadcasts: (site: string, eventId: string, status: "scheduled" | "done") =>
    request<{ broadcasts: Broadcast[] }>(
      `/v1/sites/${site}/events/${eventId}/broadcasts?status=${status}`,
    ),

  /** A waiting send, rewritten. 409 once it has started or was cancelled. */
  changeBroadcast: (
    site: string,
    eventId: string,
    id: string,
    audience: Record<string, string>,
    draft: BroadcastDraft,
  ) => {
    const query = new URLSearchParams(audience).toString();
    return request<Broadcast>(
      `/v1/sites/${site}/events/${eventId}/broadcasts/${id}${query ? `?${query}` : ""}`,
      { method: "PATCH", body: json(draft) },
    );
  },

  cancelBroadcast: (site: string, eventId: string, id: string) =>
    request<Broadcast>(`/v1/sites/${site}/events/${eventId}/broadcasts/${id}/cancel`, {
      method: "POST",
    }),

  /* -- Groups (D-74): an event's own, or the company's when `at` is absent -- */

  groups: (at?: GroupPlace) => request<{ groups: AudienceGroup[] }>(`${groupsBase(at)}`),

  group: (id: string, at?: GroupPlace) =>
    request<AudienceGroupDetail>(`${groupsBase(at)}/${id}`),

  createGroup: (name: string, at?: GroupPlace) =>
    request<AudienceGroupDetail>(groupsBase(at), { method: "POST", body: json({ name }) }),

  renameGroup: (id: string, name: string, at?: GroupPlace) =>
    request<AudienceGroupDetail>(`${groupsBase(at)}/${id}`, {
      method: "PATCH",
      body: json({ name }),
    }),

  deleteGroup: (id: string, at?: GroupPlace) =>
    request<void>(`${groupsBase(at)}/${id}`, { method: "DELETE" }),

  addToGroup: (
    id: string,
    members: { person_ids?: string[]; registration_ids?: string[] },
    at?: GroupPlace,
  ) =>
    request<GroupAdded>(`${groupsBase(at)}/${id}/members`, {
      method: "POST",
      body: json(members),
    }),

  removeFromGroup: (id: string, member: string, at?: GroupPlace) =>
    request<void>(`${groupsBase(at)}/${id}/members/${member}`, { method: "DELETE" }),

  /** Everyone the attendee filter matches, into a group: this event's, or the
   * company's for an administrator. */
  groupFromFilter: (site: string, eventId: string, id: string, filter: AttendeeFilter) => {
    const query = filterParams(filter).toString();
    return request<GroupAdded>(
      `/v1/sites/${site}/events/${eventId}/groups/${id}/from-filter${query ? `?${query}` : ""}`,
      { method: "POST" },
    );
  },

  /* -- Saved messages (D-74), the company's -- */

  savedMessages: () => request<{ saved_messages: SavedMessage[] }>("/v1/saved-messages"),

  saveMessage: (input: SavedMessageInput) =>
    request<SavedMessage>("/v1/saved-messages", { method: "POST", body: json(input) }),

  changeSavedMessage: (id: string, input: SavedMessageInput) =>
    request<SavedMessage>(`/v1/saved-messages/${id}`, { method: "PATCH", body: json(input) }),

  deleteSavedMessage: (id: string) =>
    request<void>(`/v1/saved-messages/${id}`, { method: "DELETE" }),

  /** The same draft, to everyone the client has reached, once each (BACKLOG 85).
   *
   * Still reached through an event, whose brand and name the message goes out
   * under. Its merge fields are only the ones everybody has, so the chips that
   * come back are fewer than an event's. */
  siteBroadcastPreview: (site: string, eventId: string, draft: BroadcastDraft & SiteAudience) =>
    request<BroadcastPreview>(`/v1/sites/${site}/events/${eventId}/broadcasts/site/preview`, {
      method: "POST",
      body: json(draft),
    }),

  sendSiteBroadcast: (site: string, eventId: string, draft: BroadcastDraft & SiteAudience) =>
    request<BroadcastKept>(`/v1/sites/${site}/events/${eventId}/broadcasts/site`, {
      method: "POST",
      body: json(draft),
    }),

  /** The client's imported lists, newest first (BACKLOG 85). */
  contactLists: (site: string) => request<ContactList[]>(`/v1/sites/${site}/contact-lists`),

  /** Import a list of people who opted in as a list, or say what it would do.
   *
   * A dry run needs only the name; the real one also needs the note on where
   * the consent came from and the importer's confirmation of it. */
  importContactList: (
    site: string,
    file: File,
    mapping: Record<string, string>,
    list: { name: string; consentSource: string; confirmed: boolean; dryRun: boolean },
  ) => {
    const body = new FormData();
    body.append("file", file);
    body.append("mapping", json(mapping));
    body.append("name", list.name);
    body.append("consent_source", list.consentSource);
    body.append("confirmed", String(list.confirmed));
    body.append("dry_run", String(list.dryRun));
    return request<ContactImportReport>(`/v1/sites/${site}/contact-lists/import`, {
      method: "POST",
      body,
    });
  },

  /** Delete a list and everybody on it: how their details leave the system. */
  deleteContactList: (site: string, listId: string) =>
    request<null>(`/v1/sites/${site}/contact-lists/${listId}`, { method: "DELETE" }),

  /** Every message this event has sent, newest first (T-27, I-7). */
  eventMessages: (site: string, eventId: string, limit = 25) =>
    request<MessageLog>(`/v1/sites/${site}/events/${eventId}/messages?limit=${limit}`),

  /** Send their confirmation again. 202: a worker delivers it, so this returns
   * before it has been sent. */
  resendConfirmation: (site: string, eventId: string, attendeeId: string) =>
    request<AttendeeMessage>(
      `/v1/sites/${site}/events/${eventId}/attendees/${attendeeId}/confirmation`,
      { method: "POST" },
    ),

  /** Every confirmed delegate who has never had one gets their confirmation
   * email. `dryRun` only counts. A real run answers 202: queued, not sent.
   * `codes`, when any, narrows it to those delegates (D-165). */
  sendConfirmations: (site: string, eventId: string, dryRun: boolean, codes: string[] = []) =>
    request<ConfirmationRun>(`/v1/sites/${site}/events/${eventId}/attendees/confirmations`, {
      method: "POST",
      body: json(codes.length > 0 ? { dry_run: dryRun, codes } : { dry_run: dryRun }),
    }),

  /** Every person's portrait at this site, keyed by person id.
   *
   * One request for a whole screen rather than one per row: a directory, a
   * faculty list and an assignment list each draw a face per person. */
  portraits: (site: string) =>
    request<Record<string, Portrait>>(
      // No site: the directory with no conference, each person's newest photo
      // (an administrator's, 2026-10-25).
      site ? `/v1/sites/${site}/people/portraits` : "/v1/people/portraits",
    ),

  /** This person's gallery, and which photograph this site has chosen; with no
   * site, the gallery with no choice made. */
  personGallery: (site: string, personId: string) =>
    request<Gallery>(
      site ? `/v1/sites/${site}/people/${personId}/photos` : `/v1/people/${personId}/photos`,
    ),

  /** This site's portrait: one of the gallery's ready photographs, or null for none. */
  choosePhoto: (site: string, personId: string, photoId: string | null) =>
    request<Gallery>(`/v1/sites/${site}/people/${personId}/photos/chosen`, {
      method: "PUT",
      body: JSON.stringify({ photo_id: photoId }),
    }),

  /** Begin, PUT to storage, complete. The photograph comes back `pending`; the
   * image worker makes it choosable a moment later. The PUT carries no
   * Authorization header, as in `uploadAsset`. */
  async uploadToGallery(personId: string, file: File): Promise<GalleryPhoto> {
    const reserved = await request<{ asset_id: string; upload_url: string }>(
      `/v1/people/${personId}/photos/uploads`,
      {
        method: "POST",
        body: json({ filename: file.name, content_type: file.type, bytes: file.size }),
      },
    );
    const put = await fetch(reserved.upload_url, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": file.type },
    });
    if (!put.ok) {
      throw new ApiError(put.status, "The file could not be uploaded. Please try again.");
    }
    return request<GalleryPhoto>(
      `/v1/people/${personId}/photos/uploads/${reserved.asset_id}/complete`,
      { method: "POST" },
    );
  },

  /** Take a photograph out of the gallery for every site. Refused with the
   * sites' names while any of them has chosen it. */
  removeGalleryPhoto: (personId: string, photoId: string) =>
    request<null>(`/v1/people/${personId}/photos/${photoId}`, { method: "DELETE" }),

  /** Who may touch this site, and how (T-29).
   *
   * `?page=settings` is a dead link in the system being replaced, so nothing
   * there manages users or roles at all (I-16). */
  members: (site: string) => request<Member[]>(`/v1/sites/${site}/members`),

  /** Grant an existing account a role. Owner only, and by address rather than
   * by id — that is what an owner knows about a colleague. Creating the account
   * stays on the CLI, because it needs a password. */
  addMember: (site: string, email: string, role: Role) =>
    request<Member>(`/v1/sites/${site}/members`, {
      method: "POST",
      body: json({ email, role }),
    }),

  /** Never the last owner: the server answers 409 and the screen does not
   * offer it. */
  changeMemberRole: (site: string, memberId: string, role: Role) =>
    request<Member>(`/v1/sites/${site}/members/${memberId}`, {
      method: "PATCH",
      body: json({ role }),
    }),

  /** Takes away access to this site. The account itself survives. */
  removeMember: (site: string, memberId: string) =>
    request<void>(`/v1/sites/${site}/members/${memberId}`, { method: "DELETE" }),

  siteSettings: (site: string) => request<SiteSettings>(`/v1/sites/${site}/settings`),

  /** What a *new* event on this site starts with. Never re-denominates an event
   * that already exists. */
  saveSiteSettings: (site: string, defaultCurrency: string) =>
    request<SiteSettings>(`/v1/sites/${site}/settings`, {
      method: "PATCH",
      body: json({ default_currency: defaultCurrency }),
    }),

  /** Where each value sits on this event's badge or certificate (T-20, T-21).
   *
   * The layout, the artwork behind it and the list of what may be placed, in
   * one response — the editor cannot render a field without all three. */
  layout: (site: string, eventId: string, kind: "badge" | "certificate") =>
    request<DocumentLayout>(`/v1/sites/${site}/events/${eventId}/layouts/${kind}`),

  /** Replace a whole layout. Whole rather than field by field: a layout is read
   * as one arrangement, and a partial write leaves a document nobody designed. */
  saveLayout: (
    site: string,
    eventId: string,
    kind: "badge" | "certificate",
    fields: PlacedFieldData[],
  ) =>
    request<DocumentLayout>(`/v1/sites/${site}/events/${eventId}/layouts/${kind}`, {
      method: "PUT",
      body: json({ fields }),
    }),

  /** Put one of the site's images behind the document's fields, or null to
   * take it away. A 409 means the image is still being processed. */
  chooseArtwork: (
    site: string,
    eventId: string,
    kind: "badge" | "certificate",
    assetId: string | null,
  ) =>
    request<DocumentLayout>(`/v1/sites/${site}/events/${eventId}/layouts/${kind}/artwork`, {
      method: "PUT",
      body: json({ asset_id: assetId }),
    }),

  /** A print run: everybody matching the filter, with their values and their QR.
   *
   * `params` is a tile's own `filter`, passed through untouched, exactly as the
   * export takes it — so "print the badges for everyone confirmed" and
   * "download everyone confirmed" are one definition of that segment rather
   * than two that can drift apart.
   */
  badgeRun: (site: string, eventId: string, params: Record<string, string> = {}) => {
    const query = new URLSearchParams(params).toString();
    return request<BadgeRun>(
      `/v1/sites/${site}/events/${eventId}/badge-run${query ? `?${query}` : ""}`,
    );
  },

  /** Every issued certificate with the values on its artwork, in one request. */
  certificateRun: (site: string, eventId: string) =>
    request<CertificateRun>(`/v1/sites/${site}/events/${eventId}/certificate-run`),

  /** Record that a whole print run came off the printer (T-20).
   *
   * One request for the run rather than one per badge: a sheet of five hundred
   * is one act at a desk, and five hundred POSTs is a progress bar nobody asked
   * for. A reprint is the same rows with a later time, not a conflict.
   */
  markBadgesPrinted: (site: string, eventId: string, registrationIds: string[]) =>
    request<{ printed: number; at: string }>(
      `/v1/sites/${site}/events/${eventId}/badges/printed`,
      { method: "POST", body: json({ registration_ids: registrationIds }) },
    ),

  /** What the badge kiosk shows (D-93): the QR a newcomer registers from, null
   * while the registration link is closed, and the queue of those who did.
   * The link names no counter (D-126): which one prints a badge is not known
   * when the phone registers, and the phone asks afterwards. */
  kiosk: (site: string, eventId: string, kioskId: string) =>
    request<KioskState>(
      `/v1/sites/${site}/events/${eventId}/kiosks/${encodeURIComponent(kioskId)}`,
    ),

  /** The kiosk prints this badge: recorded as printed once, under a lock, and
   * answered with the badge to print. A second ask is a 409, so two counters
   * cannot both print it. `printer`, this counter's number, is kept with the
   * badge for the sign-up screen and the delegate's phone to name (D-126). */
  printAtKiosk: (
    site: string,
    eventId: string,
    kioskId: string,
    attendeeId: string,
    printer: number | null,
  ) =>
    request<BadgeRun>(
      `/v1/sites/${site}/events/${eventId}/kiosks/${encodeURIComponent(kioskId)}/badges/${attendeeId}${printer === null ? "" : `?printer=${printer}`}`,
      { method: "POST" },
    ),

  /** Record that one badge came off the printer. A reprint is the same row with
   * a later time, not a conflict. */
  markBadgePrinted: (site: string, eventId: string, attendeeId: string) =>
    request<{ registration_id: string; printed_at: string }>(
      `/v1/sites/${site}/events/${eventId}/attendees/${attendeeId}/badge`,
      { method: "POST" },
    ),

  /** The desk has seen their ID: who and when, so the badge is made. */
  checkId: (site: string, eventId: string, attendeeId: string) =>
    request<{ checked_at: string; checked_by: string }>(
      `/v1/sites/${site}/events/${eventId}/attendees/${attendeeId}/id-check`,
      { method: "POST" },
    ),

  /** One issued certificate as the values that go on the artwork. */
  certificateDocument: (site: string, eventId: string, certificateId: string) =>
    request<CertificateDocument>(
      `/v1/sites/${site}/events/${eventId}/certificates/${certificateId}/document`,
    ),

  /** One box, any badge, any event of this site (T-22).
   *
   * Scoped to the site rather than the installation: a code from another
   * client's event answers 404, the same as one nobody ever issued. */
  scanLookup: (site: string, code: string) =>
    request<ScanLookup>(`/v1/sites/${site}/scan?code=${encodeURIComponent(code)}`),

  /** One registration in full: answers, door log and workshops. */
  attendee: (site: string, eventId: string, attendeeId: string) =>
    request<AttendeeRecordData>(`/v1/sites/${site}/events/${eventId}/attendees/${attendeeId}`),

  /** Correct somebody's answers, or move their status. The answers go through
   * the event's own form, so an edit cannot produce a row a registration could
   * not have produced. */
  updateAttendee: (site: string, eventId: string, attendeeId: string, patch: AttendeePatch) =>
    request<AttendeeRecordData>(`/v1/sites/${site}/events/${eventId}/attendees/${attendeeId}`, {
      method: "PATCH",
      body: json(patch),
    }),

  /** Who WhatsApp cannot reach for want of a country code (D-83). */
  unreachable: (site: string, eventId: string) =>
    request<UnreachableAttendee[]>(`/v1/sites/${site}/events/${eventId}/attendees/unreachable`),

  /** Delete a registration that has no attendance behind it. A 409 with the
   * scan count comes back where there is. */
  deleteAttendee: (site: string, eventId: string, attendeeId: string) =>
    request<void>(`/v1/sites/${site}/events/${eventId}/attendees/${attendeeId}`, {
      method: "DELETE",
    }),

  /** One check-in or check-out.
   *
   * `client_token` is the whole of the offline story: the server answers 200
   * with `recorded: false` for a replay rather than a conflict, so a queue
   * draining after a reconnect reads the same as one sent online. */
  recordScan: (site: string, eventId: string, scan: ScanInput) =>
    request<ScanResult>(`/v1/sites/${site}/events/${eventId}/scans`, {
      method: "POST",
      body: json(scan),
    }),

  workshops: (site: string, eventId: string) =>
    request<Workshop[]>(`/v1/sites/${site}/events/${eventId}/workshops`),

  evaluationForm: (site: string, eventId: string) =>
    request<EvaluationForm>(`/v1/sites/${site}/events/${eventId}/evaluation-form`),

  /** The additions only. The five are not the client's to send — the server
   * refuses a declaration that reuses one of their names. */
  saveEvaluationForm: (site: string, eventId: string, extra: RegistrationField[]) =>
    request<EvaluationForm>(`/v1/sites/${site}/events/${eventId}/evaluation-form`, {
      method: "PUT",
      body: json({ extra }),
    }),

  evaluationResults: (site: string, eventId: string) =>
    request<EvaluationResults>(`/v1/sites/${site}/events/${eventId}/evaluation-results`),

  /** Open a session for evaluation, replacing any link it had. Issuing and
   * rotating are one operation: a session with two live links is a thing
   * everybody would then have to reason about. */
  issueEvaluationLink: (site: string, eventId: string, sessionId: string) =>
    request<EvaluationLink>(
      `/v1/sites/${site}/events/${eventId}/sessions/${sessionId}/evaluation-link`,
      { method: "POST" },
    ),

  /** The session's evaluation link as it stands, without issuing a new one,
   * which would stop every QR already printed (D-86). */
  evaluationLink: (site: string, eventId: string, sessionId: string) =>
    request<EvaluationLink>(
      `/v1/sites/${site}/events/${eventId}/sessions/${sessionId}/evaluation-link`,
    ),

  /** Close evaluation. The link stops resolving; the answers stay. */
  revokeEvaluationLink: (site: string, eventId: string, sessionId: string) =>
    request<EvaluationLink>(
      `/v1/sites/${site}/events/${eventId}/sessions/${sessionId}/evaluation-link`,
      { method: "DELETE" },
    ),

  /** The event's agenda, in the order it runs. */
  sessions: (site: string, eventId: string) =>
    request<EventSession[]>(`/v1/sites/${site}/events/${eventId}/sessions`),

  createSession: (site: string, eventId: string, input: SessionInput) =>
    request<EventSession>(`/v1/sites/${site}/events/${eventId}/sessions`, {
      method: "POST",
      body: json(input),
    }),

  /** Sends every field, `speaker_ids` included. The server leaves speakers
   * alone when the key is absent, so a partial send would be a panel silently
   * kept where the form meant to change it. */
  updateSession: (site: string, eventId: string, id: string, input: SessionInput) =>
    request<EventSession>(`/v1/sites/${site}/events/${eventId}/sessions/${id}`, {
      method: "PATCH",
      body: json(input),
    }),

  deleteSession: (site: string, eventId: string, id: string) =>
    request<void>(`/v1/sites/${site}/events/${eventId}/sessions/${id}`, {
      method: "DELETE",
    }),

  createWorkshop: (site: string, eventId: string, input: WorkshopInput) =>
    request<Workshop>(`/v1/sites/${site}/events/${eventId}/workshops`, {
      method: "POST",
      body: json(input),
    }),

  updateWorkshop: (site: string, eventId: string, id: string, input: WorkshopInput) =>
    request<Workshop>(`/v1/sites/${site}/events/${eventId}/workshops/${id}`, {
      method: "PATCH",
      body: json(input),
    }),

  deleteWorkshop: (site: string, eventId: string, id: string) =>
    request<void>(`/v1/sites/${site}/events/${eventId}/workshops/${id}`, {
      method: "DELETE",
    }),

  /** Take a seat for one badge. A full workshop answers 409 rather than
   * quietly accepting a 41st person for a room with 40 chairs. */
  bookWorkshop: (site: string, eventId: string, id: string, code: string) =>
    request<Workshop>(`/v1/sites/${site}/events/${eventId}/workshops/${id}/bookings`, {
      method: "POST",
      body: json({ code }),
    }),

  coupons: (site: string, eventId: string, batch?: string) =>
    request<Coupon[]>(
      `/v1/sites/${site}/events/${eventId}/coupons${
        batch ? `?batch=${encodeURIComponent(batch)}` : ""
      }`,
    ),

  /** One named code, or a batch sharing a recipient and a reason. Both are the
   * same call because they differ only in where the code comes from. */
  createCoupons: (site: string, eventId: string, input: CouponInput) =>
    request<Coupon[]>(`/v1/sites/${site}/events/${eventId}/coupons`, {
      method: "POST",
      body: json(input),
    }),

  deleteCoupon: (site: string, eventId: string, id: string) =>
    request<void>(`/v1/sites/${site}/events/${eventId}/coupons/${id}`, {
      method: "DELETE",
    }),

  /** What the installation is doing right now (T-37). Admin-only: these are
   * figures about the machine and the process, not about one client's site. */
  systemStatus: () => request<SystemStatus>("/v1/status"),

  /** What each route has cost since this process started. */
  systemRoutes: () => request<RouteLoadReport>("/v1/status/routes"),
  /** The readings the worker kept a minute at a time, for the last `hours`. */
  systemHistory: (hours: number) => request<SystemHistory>(`/v1/status/history?hours=${hours}`),
  /** What rust-worker's jobs and every marked section of code have allocated. */
  systemMemory: () => request<MemoryReport>("/v1/status/memory"),
  /** What each storage holds, S3 and R2 apart, and what keeping it costs (D-150). */
  systemStorage: () => request<StorageReport>("/v1/status/storage"),
  /** How many days the backup keeps the copy of a deleted file (BACKLOG 284). */
  backupRetention: () => request<BackupRetention>("/v1/status/backup-retention"),
  setBackupRetention: (days: number) =>
    request<BackupRetention>("/v1/status/backup-retention", {
      method: "PUT",
      body: json({ days }),
    }),
  /** Python's lines whose memory grew in a window of up to a minute; rust-api
   * answers at once with its sections. */
  profileMemory: () => request<MemoryProfile>("/v1/status/memory-profile", { method: "POST" }),
  /** What is using the machine now, by service (D-81); takes half a second. */
  systemResources: () => request<Resources>("/v1/status/resources"),

  /** The Learn Rust tutorial, as the HTML document it is (administrators only). */
  learnRust: () => request<string>("/v1/learn/rust"),

  /** Which API answers, every fallback to Python and every 5xx (D-48). */
  systemFallbacks: () => request<FallbackReport>("/v1/status/fallbacks"),

  /** What the queue ran, newest first (D-77). */
  systemJobs: (filter: { status?: JobStatus | undefined; kind?: string | undefined }) => {
    const params = new URLSearchParams();
    if (filter.status) params.set("status", filter.status);
    if (filter.kind) params.set("kind", filter.kind);
    const query = params.toString();
    return request<JobLog>(`/v1/status/jobs${query ? `?${query}` : ""}`);
  },

  /** Queue one kind's failed jobs again, from their first attempt (BACKLOG 65).
   * `since` is ISO 8601 with its zone; absent means whenever they were queued. */
  retryFailedJobs: (kind: string, since?: string) =>
    request<{ requeued: number }>("/v1/status/jobs/retry", {
      method: "POST",
      body: json({ kind, since: since ?? null }),
    }),

  certificates: (site: string, eventId: string) =>
    request<Certificate[]>(`/v1/sites/${site}/events/${eventId}/certificates`),

  /** The points are not sent. They are read from the scans, and a field for
   * them here would be the defect I-6 describes in a nicer font. */
  issueCertificate: (
    site: string,
    eventId: string,
    input: { code: string; category?: string; accreditation_number?: string },
  ) =>
    request<Certificate>(`/v1/sites/${site}/events/${eventId}/certificates`, {
      method: "POST",
      body: json(input),
    }),

  registrationLink: (site: string, eventId: string) =>
    request<RegistrationLink>(`/v1/sites/${site}/events/${eventId}/registration-link`),

  /** Issuing replaces any link the event had: rotation *is* revocation of the
   * previous one, which is how a link shared too widely is withdrawn. */
  issueRegistrationLink: (site: string, eventId: string) =>
    request<RegistrationLink>(`/v1/sites/${site}/events/${eventId}/registration-link`, {
      method: "POST",
    }),

  revokeRegistrationLink: (site: string, eventId: string) =>
    request<RegistrationLink>(`/v1/sites/${site}/events/${eventId}/registration-link`, {
      method: "DELETE",
    }),

  /* -- Profile links and their queue (T-38) ------------------------------ */

  profileLinks: () => request<ProfileLink[]>("/v1/people/forms"),

  /** The events a form may be for: every conference's the caller may write. */
  formEvents: () => request<FormEvent[]>("/v1/people/form-events"),

  createProfileLink: (input: ProfileLinkInput) =>
    request<ProfileLink>("/v1/people/forms", {
      method: "POST",
      body: json(input),
    }),

  /** Issuing a new token retires the old one: rotation *is* revocation, which
   * is how a link that has been forwarded too widely is withdrawn. */
  reissueProfileLink: (formId: string) =>
    request<ProfileLink>(`/v1/people/forms/${formId}/reissue`, {
      method: "POST",
    }),

  closeProfileLink: (formId: string) =>
    request<ProfileLink>(`/v1/people/forms/${formId}/close`, {
      method: "POST",
    }),

  /** An edit of a form not issued to one person (D-95). The link stays, so
   * nobody has to be sent a new one. */
  updateProfileLink: (formId: string, patch: ProfileLinkPatch) =>
    request<ProfileLink>(`/v1/people/forms/${formId}`, {
      method: "PATCH",
      body: json(patch),
    }),

  profileSubmissions: (state?: SubmissionStatus) =>
    request<ProfileSubmission[]>(`/v1/people/answers${state ? `?state=${state}` : ""}`),

  /** `personId` is the answer to a duplicate address: the submission was an
   * add, the address is already somebody's, and an editor who has been shown
   * both says it is the same human. */
  acceptProfileSubmission: (id: string, personId?: string) =>
    request<ProfileSubmission>(`/v1/people/answers/${id}/accept`, {
      method: "POST",
      body: json({ person_id: personId ?? null }),
    }),

  declineProfileSubmission: (id: string, reason: string) =>
    request<ProfileSubmission>(`/v1/people/answers/${id}/decline`, {
      method: "POST",
      body: json({ reason }),
    }),

  /* -- An event's design (D-103) ----------------------------------------- */

  eventDesign: (site: string, eventId: string) =>
    request<Design>(`/v1/sites/${site}/events/${eventId}/design`),

  /** A PUT of the whole design, as the branding is saved: a partial update
   * would make "clear the banner" and "leave it alone" the same request. */
  saveEventDesign: (site: string, eventId: string, design: DesignInput) =>
    request<Design>(`/v1/sites/${site}/events/${eventId}/design`, {
      method: "PUT",
      body: json(design),
    }),

  /* -- The running order, for the Q&A moderator (BACKLOG 263) ------------- */

  runningOrder: (site: string, eventId: string) =>
    request<RunningOrder>(`/v1/sites/${site}/events/${eventId}/running-order`),

  /** The whole document, as the design is saved: the server sorts it. */
  saveRunningOrder: (site: string, eventId: string, document: RoDocument) =>
    request<RunningOrder>(`/v1/sites/${site}/events/${eventId}/running-order`, {
      method: "PUT",
      body: json(document),
    }),

  /** `item: null` clears the marker. */
  setOnStage: (
    site: string,
    eventId: string,
    item: string | null,
    talk: string | null = null,
  ) =>
    request<RunningOrder>(`/v1/sites/${site}/events/${eventId}/running-order/on-stage`, {
      method: "PUT",
      body: json(item === null ? { item } : { item, talk }),
    }),

  /* -- Branding (T-38, the half of T-29 that had no model) --------------- */

  branding: (site: string) => request<Branding>(`/v1/sites/${site}/branding`),

  /** The brand a form made for this event shows whoever opens it, for the
   * People popup's preview (D-95). */
  eventBrand: (site: string, eventId: string) =>
    request<Brand>(`/v1/sites/${site}/events/${eventId}/brand`),

  /** A PUT, not a PATCH: one small record edited whole on one screen, where a
   * partial update would make "clear the logo" and "leave it alone" the same
   * request. */
  saveBranding: (
    site: string,
    input: {
      colour: string | null;
      logo_asset_id: string | null;
      letterhead_asset_id: string | null;
      display_name: string | null;
      footer: string | null;
    },
  ) => request<Branding>(`/v1/sites/${site}/branding`, { method: "PUT", body: json(input) }),

  /* -- The Notify drip (T-39) -------------------------------------------- */

  notifySchedule: (site: string, eventId: string) =>
    request<NotifySchedule>(`/v1/sites/${site}/events/${eventId}/notify`),

  saveNotifySchedule: (site: string, eventId: string, input: NotifyScheduleInput) =>
    request<NotifySchedule>(`/v1/sites/${site}/events/${eventId}/notify`, {
      method: "PUT",
      body: json(input),
    }),

  /* -- The team workspace (D-55): administrators only ---------------------- */

  /** The one board, by status then position; narrowed to a conference
   * (`"none"`: to tasks on none) or to the caller's own. */
  teamTasks: (
    filter: { event?: string; assignee?: "me"; block?: string; session?: string } = {},
  ) => {
    const query = new URLSearchParams();
    if (filter.event) query.set("event", filter.event);
    if (filter.block) query.set("block", filter.block);
    if (filter.session) query.set("session", filter.session);
    if (filter.assignee) query.set("assignee", filter.assignee);
    const text = query.toString();
    return request<TeamTask[]>(`/v1/team/tasks${text ? `?${text}` : ""}`);
  },

  createTeamTask: (input: TeamTaskInput) =>
    request<TeamTask>("/v1/team/tasks", { method: "POST", body: json(input) }),

  changeTeamTask: (taskId: string, change: TeamTaskChange) =>
    request<TeamTask>(`/v1/team/tasks/${encodeURIComponent(taskId)}`, {
      method: "PATCH",
      body: json(change),
    }),

  deleteTeamTask: (taskId: string) =>
    request<null>(`/v1/team/tasks/${encodeURIComponent(taskId)}`, { method: "DELETE" }),

  teamComments: (taskId: string) =>
    request<TeamComment[]>(`/v1/team/tasks/${encodeURIComponent(taskId)}/comments`),

  addTeamComment: (taskId: string, body: string) =>
    request<TeamComment>(`/v1/team/tasks/${encodeURIComponent(taskId)}/comments`, {
      method: "POST",
      body: json({ body }),
    }),

  deleteTeamComment: (taskId: string, commentId: string) =>
    request<null>(
      `/v1/team/tasks/${encodeURIComponent(taskId)}/comments/${encodeURIComponent(commentId)}`,
      { method: "DELETE" },
    ),

  /** Who a card can be given to: every active administrator. */
  teamPeople: () => request<TeamPerson[]>("/v1/team/people"),

  /** Every conference a task can be linked to, newest first. */
  teamConferences: () => request<TeamConference[]>("/v1/team/events"),

  /* -- Blocks (D-70): sessions behind an attendance QR and a question QR -- */

  blocks: (site: string, eventId: string) =>
    request<{ blocks: Block[] }>(`/v1/sites/${site}/events/${eventId}/blocks`),

  blockCandidates: (site: string, eventId: string) =>
    request<BlockCandidates>(`/v1/sites/${site}/events/${eventId}/blocks/candidates`),

  createBlock: (site: string, eventId: string, input: BlockInput) =>
    request<Block>(`/v1/sites/${site}/events/${eventId}/blocks`, {
      method: "POST",
      body: json(input),
    }),

  changeBlock: (site: string, eventId: string, blockId: string, input: BlockInput) =>
    request<Block>(
      `/v1/sites/${site}/events/${eventId}/blocks/${encodeURIComponent(blockId)}`,
      { method: "PATCH", body: json(input) },
    ),

  deleteBlock: (site: string, eventId: string, blockId: string) =>
    request<null>(`/v1/sites/${site}/events/${eventId}/blocks/${encodeURIComponent(blockId)}`, {
      method: "DELETE",
    }),

  /** A block for every session in none yet. */
  blocksPerSession: (site: string, eventId: string) =>
    request<{ made: number }>(`/v1/sites/${site}/events/${eventId}/blocks/per-session`, {
      method: "POST",
    }),

  /** Three new links; the printed QRs stop working. */
  newBlockLinks: (site: string, eventId: string, blockId: string) =>
    request<Block>(
      `/v1/sites/${site}/events/${eventId}/blocks/${encodeURIComponent(blockId)}/links`,
      { method: "POST" },
    ),

  blockPolls: (site: string, eventId: string, blockId: string) =>
    request<{ polls: BlockPoll[] }>(
      `/v1/sites/${site}/events/${eventId}/blocks/${encodeURIComponent(blockId)}/polls`,
    ),

  createPoll: (
    site: string,
    eventId: string,
    blockId: string,
    input: { question: string; options: string[] },
  ) =>
    request<{ polls: BlockPoll[] }>(
      `/v1/sites/${site}/events/${eventId}/blocks/${encodeURIComponent(blockId)}/polls`,
      { method: "POST", body: json(input) },
    ),

  setPoll: (
    site: string,
    eventId: string,
    blockId: string,
    pollId: string,
    status: "open" | "closed",
  ) =>
    request<{ polls: BlockPoll[] }>(
      `/v1/sites/${site}/events/${eventId}/blocks/${encodeURIComponent(blockId)}/polls/${encodeURIComponent(pollId)}`,
      { method: "PATCH", body: json({ status }) },
    ),

  deletePoll: (site: string, eventId: string, blockId: string, pollId: string) =>
    request<null>(
      `/v1/sites/${site}/events/${eventId}/blocks/${encodeURIComponent(blockId)}/polls/${encodeURIComponent(pollId)}`,
      { method: "DELETE" },
    ),

  /* -- Votes, questionnaires and lucky draws (D-183) -- */

  takeParts: (site: string, eventId: string) =>
    request<{ items: TakePartItem[] }>(`/v1/sites/${site}/events/${eventId}/take-part`),

  makeTakePart: (site: string, eventId: string, input: { kind: TakePartKind; title: string }) =>
    request<TakePartDetail>(`/v1/sites/${site}/events/${eventId}/take-part`, {
      method: "POST",
      body: json(input),
    }),

  takePart: (site: string, eventId: string, id: string) =>
    request<TakePartDetail>(
      `/v1/sites/${site}/events/${eventId}/take-part/${encodeURIComponent(id)}`,
    ),

  editTakePart: (site: string, eventId: string, id: string, edit: TakePartEdit) =>
    request<TakePartDetail>(
      `/v1/sites/${site}/events/${eventId}/take-part/${encodeURIComponent(id)}`,
      { method: "PATCH", body: json(edit) },
    ),

  takePartStatus: (site: string, eventId: string, id: string, status: "open" | "closed") =>
    request<TakePartDetail>(
      `/v1/sites/${site}/events/${eventId}/take-part/${encodeURIComponent(id)}/status`,
      { method: "POST", body: json({ status }) },
    ),

  /** A new public link; the old one stops working. */
  takePartLink: (site: string, eventId: string, id: string) =>
    request<TakePartDetail>(
      `/v1/sites/${site}/events/${eventId}/take-part/${encodeURIComponent(id)}/link`,
      { method: "POST" },
    ),

  /** Every vote, answer, entry and winner deleted, as after a rehearsal. */
  resetTakePart: (site: string, eventId: string, id: string) =>
    request<TakePartDetail>(
      `/v1/sites/${site}/events/${eventId}/take-part/${encodeURIComponent(id)}/reset`,
      { method: "POST" },
    ),

  deleteTakePart: (site: string, eventId: string, id: string) =>
    request<null>(`/v1/sites/${site}/events/${eventId}/take-part/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),

  /** A closed draw's next winner, or with `redraw` its last one drawn again. */
  drawWinner: (site: string, eventId: string, id: string, redraw: boolean) =>
    request<TakePartDetail>(
      `/v1/sites/${site}/events/${eventId}/take-part/${encodeURIComponent(id)}/draw`,
      { method: "POST", body: json({ redraw }) },
    ),

  takePartSheet: (site: string, eventId: string, id: string) =>
    download(
      `/v1/sites/${site}/events/${eventId}/take-part/${encodeURIComponent(id)}/export`,
      "take-part.csv",
    ),

  attendance: (site: string, eventId: string, of: { block: string } | { session: string }) =>
    request<AttendanceSheet>(
      "block" in of
        ? `/v1/sites/${site}/events/${eventId}/blocks/${encodeURIComponent(of.block)}/attendance`
        : `/v1/sites/${site}/events/${eventId}/sessions/${encodeURIComponent(of.session)}/attendance`,
    ),

  /** The spreadsheet handed to the organiser. Audited: it carries phone numbers. */
  exportAttendance: (
    site: string,
    eventId: string,
    of: { block: string } | { session: string },
  ) =>
    download(
      "block" in of
        ? `/v1/sites/${site}/events/${eventId}/blocks/${encodeURIComponent(of.block)}/attendance.csv`
        : `/v1/sites/${site}/events/${eventId}/sessions/${encodeURIComponent(of.session)}/attendance.csv`,
      "attendance.csv",
    ),

  /* -- Faculty travel and hotel (D-71) -- */

  travel: (site: string, eventId: string) =>
    request<TravelBoard>(`/v1/sites/${site}/events/${eventId}/travel`),

  saveTravel: (site: string, eventId: string, personId: string, input: TravelInput) =>
    request<TravelPlan>(
      `/v1/sites/${site}/events/${eventId}/travel/${encodeURIComponent(personId)}`,
      { method: "PUT", body: json(input) },
    ),

  clearTravel: (site: string, eventId: string, personId: string) =>
    request<null>(
      `/v1/sites/${site}/events/${eventId}/travel/${encodeURIComponent(personId)}`,
      { method: "DELETE" },
    ),

  /** The hotel's rooming list or the transfer company's arrivals. Audited. */
  exportTravel: (site: string, eventId: string, sheet: "rooming" | "arrivals") =>
    download(`/v1/sites/${site}/events/${eventId}/travel/${sheet}.csv`, `${sheet}.csv`),

  /** Where each of the event's speakers has got to (D-69). */
  onboarding: (site: string, eventId: string) =>
    request<OnboardingBoard>(`/v1/sites/${site}/events/${eventId}/onboarding`),

  saveWelcomeText: (site: string, eventId: string, text: { subject: string; body: string }) =>
    request<WelcomeText>(`/v1/sites/${site}/events/${eventId}/onboarding/welcome-text`, {
      method: "PUT",
      body: json(text),
    }),

  resetWelcomeText: (site: string, eventId: string) =>
    request<null>(`/v1/sites/${site}/events/${eventId}/onboarding/welcome-text`, {
      method: "DELETE",
    }),

  previewWelcome: (site: string, eventId: string, personId: string) =>
    request<WelcomePreview>(`/v1/sites/${site}/events/${eventId}/onboarding/welcome/preview`, {
      method: "POST",
      body: json({ person_id: personId }),
    }),

  /** One email each, to the speakers named, or to everyone not yet welcomed. */
  sendWelcome: (site: string, eventId: string, to: string[] | "everyone") =>
    request<WelcomeSent>(`/v1/sites/${site}/events/${eventId}/onboarding/welcome`, {
      method: "POST",
      body: json(to === "everyone" ? { everyone: true } : { person_ids: to }),
    }),

  /** One Kuwait day of what anybody changed, newest first (D-66). */
  activityLog: (filters: ActivityFilters) => {
    const query = new URLSearchParams();
    for (const [name, value] of Object.entries(filters)) if (value) query.set(name, value);
    const suffix = query.size ? `?${query}` : "";
    return request<ActivityDay>(`/v1/activity${suffix}`);
  },

  /** One Kuwait day of attempts to pay, failed ones too, newest first (D-189). */
  paymentsLog: (date: string) =>
    request<PaymentsLogDay>(`/v1/payments/log?date=${encodeURIComponent(date)}`),

  /** Every administrator, the invitations still waiting among them. */
  teamMembers: () => request<TeamMember[]>("/v1/team/members"),

  /** Invite an administrator by address; a waiting invitation is sent again. */
  inviteMember: (email: string, siteKey?: string) =>
    request<TeamMember>("/v1/team/members", {
      method: "POST",
      // With a conference, the account is its registration desk (D-88).
      body: json(siteKey ? { email, site_key: siteKey } : { email }),
    }),

  /** Switch an administrator's account off (signed out everywhere) or back on. */
  switchMember: (memberId: string, isActive: boolean) =>
    request<TeamMember>(`/v1/team/members/${encodeURIComponent(memberId)}`, {
      method: "PATCH",
      body: json({ is_active: isActive }),
    }),

  /** Withdraw a waiting invitation; an account in use is a 404. */
  cancelInvite: (memberId: string) =>
    request<null>(`/v1/team/members/${encodeURIComponent(memberId)}`, { method: "DELETE" }),

  /** Every registration desk account, one row per conference it works (D-127). */
  teamDesks: () => request<DeskAccount[]>("/v1/team/desks"),

  /** A desk account made on the spot: no email, switched on at once. */
  makeDesk: (body: DeskAccountIn) =>
    request<DeskAccount>("/v1/team/desks", { method: "POST", body: json(body) }),

  /** A desk account's username, name or password changed, or it switched off or on. */
  changeDesk: (deskId: string, patch: DeskAccountPatch) =>
    request<DeskAccount>(`/v1/team/desks/${encodeURIComponent(deskId)}`, {
      method: "PATCH",
      body: json(patch),
    }),

  /** Every folder, for the pickers and the move dialog. */
  storageTree: () => request<TeamTreeFolder[]>("/v1/team/storage/tree"),

  /** A folder's trail, folders and files; null is the top level. */
  storageFolder: (folderId: string | null) =>
    request<TeamFolderView>(
      `/v1/team/storage/folders/${folderId === null ? "root" : encodeURIComponent(folderId)}`,
    ),

  createFolder: (input: { name: string; parent_id: string | null }) =>
    request<TeamFolder>("/v1/team/storage/folders", { method: "POST", body: json(input) }),

  changeFolder: (folderId: string, change: TeamFolderChange) =>
    request<TeamFolder>(`/v1/team/storage/folders/${encodeURIComponent(folderId)}`, {
      method: "PATCH",
      body: json(change),
    }),

  /** Refused with 409 while it holds a folder or a file. */
  deleteFolder: (folderId: string) =>
    request<null>(`/v1/team/storage/folders/${encodeURIComponent(folderId)}`, {
      method: "DELETE",
    }),

  beginStorageFile: (body: { filename: string; bytes: number; folder_id: string | null }) =>
    request<FmFilesPending>("/v1/team/storage/files", { method: "POST", body: json(body) }),

  finishStorageFile: (fileId: string) =>
    request<TeamStorageFile>(`/v1/team/storage/files/${encodeURIComponent(fileId)}/finish`, {
      method: "POST",
    }),

  storageFileDownload: (fileId: string) =>
    request<FmFileDownload>(`/v1/team/storage/files/${encodeURIComponent(fileId)}/download`),

  changeStorageFile: (fileId: string, change: TeamStorageFileChange) =>
    request<TeamStorageFile>(`/v1/team/storage/files/${encodeURIComponent(fileId)}`, {
      method: "PATCH",
      body: json(change),
    }),

  deleteStorageFile: (fileId: string) =>
    request<null>(`/v1/team/storage/files/${encodeURIComponent(fileId)}`, { method: "DELETE" }),
};

/** How many times a public request refused as busy is asked again. */
const BUSY_RETRIES = 2;

/** A refusal for capacity, which says when to come back. */
function isBusy(response: Response): boolean {
  return response.status === 503 && response.headers.has("Retry-After");
}

/** The wait a busy answer names, in milliseconds, held to a few seconds. */
function retryAfterMs(response: Response): number {
  const seconds = Number(response.headers.get("Retry-After"));
  return Number.isFinite(seconds) && seconds > 0 ? Math.min(seconds, 5) * 1000 : 1000;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * The half of this client that runs with no session (T-38, T-39).
 *
 * Separate from `api` above, and the separation is the point rather than
 * tidiness. Every method on `api` goes through `request`, which attaches the
 * bearer token and, on a 401, refreshes it — and a *public* page has no token
 * to attach and nothing to refresh, so a 404 from a closed link would send it
 * round a refresh that cannot succeed before failing anyway.
 *
 * These call `fetch` through `publicRequest`, which does the error unpacking
 * and none of the session handling. A public page that accidentally called
 * `api.something` would be reaching for a credential it does not have, and
 * keeping the two objects apart is what makes that visible in review.
 */
async function publicRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const send = () =>
    fetch(path, {
      ...init,
      headers: {
        ...(init.body ? { "Content-Type": "application/json" } : {}),
        ...init.headers,
      },
    });
  let response = await send();
  // A hall voting or scanning at once can find every public place taken: the
  // server refuses the overflow at once with 503 and Retry-After, before it
  // has done anything, so asking again is safe. Twice, after the wait it names
  // and a little more each time so a room does not come back in step (D-85).
  for (let retry = 1; retry <= BUSY_RETRIES && isBusy(response); retry++) {
    await sleep(retryAfterMs(response) * retry + Math.random() * 500);
    response = await send();
  }
  const body = await parseBody(response);
  if (response.status === 422) throw new ValidationError(problemsFrom(body), body);
  if (!response.ok) {
    throw new ApiError(response.status, messageFrom(body, response.statusText), body);
  }
  return body as T;
}

export const publicApi = {
  /** What this event asks and what it costs now (T-6, D-29). */
  registrationForm: (token: string) =>
    publicRequest<PublicRegistrationForm>(`/v1/register/${encodeURIComponent(token)}`),

  /** One registration. The fee band and a coupon travel beside the answers, as
   * `registration_type_id` and `coupon_code`, because neither is a question. */
  register: (token: string, values: Record<string, unknown>) =>
    publicRequest<RegistrationConfirmation | CheckoutStarted>(
      `/v1/register/${encodeURIComponent(token)}`,
      { method: "POST", body: json(values) },
    ),

  /** How a payer's checkout stands: the page to pay on while open, the booking
   * once paid, or that it ended unpaid (D-94). `ref` is the payer's alone. */
  checkout: (ref: string) =>
    publicRequest<CheckoutStatus>(`/v1/checkouts/${encodeURIComponent(ref)}`),

  /** Whether the badge of this link's registration with this code has printed,
   * and at which counter (D-126): asked by the phone that registered from a
   * sign-up screen's QR. */
  badgeWhere: (token: string, code: string) =>
    publicRequest<BadgeWhere>(
      `/v1/register/${encodeURIComponent(token)}/badges/${encodeURIComponent(code)}`,
    ),

  /** The call for abstracts behind this link, and the rules a file must meet. */
  abstractCall: (token: string) =>
    publicRequest<PublicCall>(`/v1/abstracts/${encodeURIComponent(token)}`),

  /** Somewhere to PUT the manuscript. Records nothing: an upload nobody
   * finishes leaves no row claiming an abstract exists. */
  beginAbstractFile: (token: string, bytes: number) =>
    publicRequest<PendingUpload>(`/v1/abstracts/${encodeURIComponent(token)}/file`, {
      method: "POST",
      body: json({ bytes }),
    }),

  submitAbstract: (token: string, body: AbstractSubmission) =>
    publicRequest<{ reference: string }>(`/v1/abstracts/${encodeURIComponent(token)}`, {
      method: "POST",
      body: json(body),
    }),

  /** Everything this reviewer has to read, and what they have already said. */
  reviewQueue: (token: string) =>
    publicRequest<ReviewerQueue>(`/v1/abstracts/review/${encodeURIComponent(token)}`),

  /** A short-lived link to one manuscript, asked for on the click. */
  reviewFile: (token: string, abstractId: string) =>
    publicRequest<FileLink>(
      `/v1/abstracts/review/${encodeURIComponent(token)}/${encodeURIComponent(abstractId)}/file`,
    ),

  /** Score one abstract. A second score corrects this reviewer's first. */
  sendReview: (
    token: string,
    abstractId: string,
    verdict: { score: number; comment: string | null },
  ) =>
    publicRequest<{ replaced: boolean; score: number }>(
      `/v1/abstracts/review/${encodeURIComponent(token)}/${encodeURIComponent(abstractId)}`,
      { method: "PUT", body: json(verdict) },
    ),

  galaInvitation: (token: string) =>
    publicRequest<PublicInvitation>(`/v1/gala/${encodeURIComponent(token)}`),

  replyToGala: (
    token: string,
    reply: {
      attending: boolean;
      guests: number;
      code: string | null;
      answers: Record<string, unknown>;
    },
  ) =>
    publicRequest<Replaced & { attending: boolean }>(`/v1/gala/${encodeURIComponent(token)}`, {
      method: "POST",
      body: json(reply),
    }),

  survey: (token: string) =>
    publicRequest<PublicSurvey>(`/v1/survey/${encodeURIComponent(token)}`),

  answerSurvey: (
    token: string,
    response: { code: string | null; answers: Record<string, unknown> },
  ) =>
    publicRequest<Replaced>(`/v1/survey/${encodeURIComponent(token)}`, {
      method: "POST",
      body: json(response),
    }),

  evaluation: (token: string) =>
    publicRequest<PublicSession>(`/v1/evaluate/${encodeURIComponent(token)}`),

  /** What an event's certificate download page shows (D-185). */
  certificatePage: (site: string, slug: string) =>
    publicRequest<CertificatePage>(
      `/v1/certificate/${encodeURIComponent(site)}/${encodeURIComponent(slug)}`,
    ),

  /** The delegate's certificate as a PDF, by their badge code or mobile, from
   * the address the survey names (D-157) or the event's download page (D-185). */
  certificate: async (path: string, code: string): Promise<Download> => {
    const response = await fetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: json({ q: code }),
    });
    if (!response.ok) {
      // The body rides along: a 409 can carry the survey to answer first.
      const body = await parseBody(response);
      throw new ApiError(response.status, messageFrom(body, response.statusText), body);
    }
    return {
      blob: await response.blob(),
      filename: filenameFrom(response.headers.get("Content-Disposition"), "certificate.pdf"),
    };
  },

  /** The badge code is required here: it is what counts a delegate once. */
  evaluate: (token: string, submission: { code: string; answers: Record<string, unknown> }) =>
    publicRequest<Replaced>(`/v1/evaluate/${encodeURIComponent(token)}`, {
      method: "POST",
      body: json(submission),
    }),

  qaPanel: (token: string) => publicRequest<PublicPanel>(`/v1/qa/${encodeURIComponent(token)}`),

  /** One question. It reaches a moderator, not the wall. */
  ask: (token: string, question: { body: string; code: string | null }) =>
    publicRequest<{ pending: boolean }>(`/v1/qa/${encodeURIComponent(token)}`, {
      method: "POST",
      body: json(question),
    }),

  qaFeed: (token: string) =>
    publicRequest<PublicFeed>(`/v1/qa/feed/${encodeURIComponent(token)}`),

  /* -- A block's QRs (D-70) -- */

  attendPage: (token: string) =>
    publicRequest<PublicAttend>(`/v1/attend/${encodeURIComponent(token)}`),

  /* -- Votes, questionnaires and lucky draws (D-183) -- */

  takePartPage: (token: string) =>
    publicRequest<PublicTakePart>(`/v1/take-part/${encodeURIComponent(token)}`),

  /** A 409 with `already: true` means this mobile took part before. */
  takePart: (
    token: string,
    input: {
      name: string;
      phone: string;
      choices?: string[];
      answers?: Record<string, number | number[] | string>;
    },
  ) =>
    publicRequest<{ done: true }>(`/v1/take-part/${encodeURIComponent(token)}`, {
      method: "POST",
      body: json(input),
    }),

  takePartHere: (token: string, input: { browser: string; phone?: string }) =>
    publicRequest<{ taken_part: boolean }>(`/v1/take-part/${encodeURIComponent(token)}/here`, {
      method: "POST",
      body: json(input),
    }),

  /** Name and phone as typed; nothing is matched to a registration. */
  attend: (token: string, input: { name: string; phone: string }) =>
    publicRequest<{ name: string; block: string; at: string }>(
      `/v1/attend/${encodeURIComponent(token)}`,
      { method: "POST", body: json(input) },
    ),

  blockQa: (token: string) =>
    publicRequest<PublicBlockPanel>(`/v1/qa/block/${encodeURIComponent(token)}`),

  /** One question, with the asker's name, to the block's moderators. */
  askBlock: (token: string, input: { name: string; body: string }) =>
    publicRequest<{ pending: boolean }>(`/v1/qa/block/${encodeURIComponent(token)}`, {
      method: "POST",
      body: json(input),
    }),

  blockFeed: (token: string) =>
    publicRequest<PublicBlockFeed>(`/v1/qa/block/feed/${encodeURIComponent(token)}`),

  /** One vote; the same voter voting again changes it (D-73). */
  vote: (token: string, input: { poll_id: string; choice: number; voter: string }) =>
    publicRequest<{ voted: number }>(`/v1/qa/block/${encodeURIComponent(token)}/vote`, {
      method: "POST",
      body: json(input),
    }),

  /** Somewhere to PUT one file for one upload question (D-30). Nothing is
   * attached until the form's own submission names the upload. */
  beginUpload: (
    form: UploadForm,
    token: string,
    input: { field: string; filename: string; bytes: number },
  ) =>
    publicRequest<PendingUpload>(`/v1/uploads/${form}/${encodeURIComponent(token)}`, {
      method: "POST",
      body: json(input),
    }),

  /** What this link asks, and whose page it is. */
  profileForm: (token: string) =>
    publicRequest<PublicProfileForm>(`/v1/profile/${encodeURIComponent(token)}`),

  /** One submission. `photo` travels beside the answers rather than among them,
   * because it is not one of the questions — the same shape a registration
   * sends its fee band in. */
  submitProfile: (token: string, values: Record<string, unknown>, photo: string | null) =>
    publicRequest<{ thanks: string }>(`/v1/profile/${encodeURIComponent(token)}`, {
      method: "POST",
      body: json(photo ? { ...values, photo } : values),
    }),

  beginProfilePhoto: (
    token: string,
    input: { filename: string; content_type: string; bytes: number },
  ) =>
    publicRequest<PhotoUpload>(`/v1/profile/${encodeURIComponent(token)}/photo`, {
      method: "POST",
      body: json(input),
    }),

  /** Tell the server the PUT landed, and ask whether it is renderable yet.
   *
   * The same route answers both, which is why the page can poll it: `land` is
   * idempotent in the way that matters — a second call re-heads the object and
   * queues a job the worker answers from the derivatives it already built. */
  finishProfilePhoto: (token: string, assetId: string) =>
    publicRequest<PhotoReady>(
      `/v1/profile/${encodeURIComponent(token)}/photo/${encodeURIComponent(assetId)}`,
      { method: "POST" },
    ),

  notifySubscribe: (site: string, slug: string, input: { email: string; name?: string }) =>
    publicRequest<{ message: string }>(
      `/v1/notify/subscribe/${encodeURIComponent(site)}/${encodeURIComponent(slug)}`,
      { method: "POST", body: json(input) },
    ),

  notifyConfirm: (token: string) =>
    publicRequest<{ message: string }>(`/v1/notify/confirm/${encodeURIComponent(token)}`, {
      method: "POST",
    }),

  notifyStop: (token: string) =>
    publicRequest<{ email: string }>(`/v1/notify/stop/${encodeURIComponent(token)}`, {
      method: "POST",
    }),
};

/**
 * Put the bytes where the server said to.
 *
 * A bare `fetch` with no credentials and no JSON header: the destination is
 * object storage on a presigned URL, and sending an Authorization header there
 * makes the signature invalid. Kept beside the two calls that surround it so
 * the three steps of an upload read as one thing.
 */
export async function putToStorage(
  url: string,
  blob: Blob,
  what = "The photograph",
): Promise<void> {
  const response = await fetch(url, {
    method: "PUT",
    // Exactly the type the URL was signed for; anything else is a 403 from the
    // store rather than an error from us.
    headers: { "Content-Type": blob.type },
    body: blob,
  });
  if (!response.ok) {
    throw new ApiError(response.status, `${what} could not be uploaded.`);
  }
}
